import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  User,
  Clock,
  DollarSign,
  Receipt,
  TrendingUp,
  TrendingDown,
  ArrowUpCircle,
  ArrowDownCircle,
  Eye,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Users,
  Banknote,
  CreditCard,
  Smartphone,
  ArrowLeftRight,
  History,
  Trash2,
} from "lucide-react";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { calculateThermalPageSize } from "../utils/thermalPrintLayout";
import { renderUsbTicketHtml } from "../utils/usbTicketTemplate";
import { buildNetPaymentBreakdown } from "../utils/paymentBreakdownDisplay";
import {
  buildJournalPaymentBreakdown,
  mergeJournalPaymentBreakdown,
  parseReserveProof,
} from "../utils/cashJournalPresentation";
import { SaleVoidDialog, SaleVoidHistoryDialog } from "./SaleVoidDialog";
import { apiFetch } from '../utils/apiClient';
import PostpaidReserveDialog from './PostpaidReserveDialog';
import { loadJournalSettlementDetails, journalTransactionKey } from '../utils/journalSettlementReceipt';

const API_BASE = "/api";

// Mapping des icônes pour les moyens de paiement
const PAYMENT_ICONS = {
  banknote: Banknote,
  "dollar-sign": DollarSign,
  "credit-card": CreditCard,
  smartphone: Smartphone,
  "arrow-left-right": ArrowLeftRight,
  receipt: Receipt,
};

const CashJournalModal = ({
  isOpen,
  onClose,
  currentUser,
  currentSession,
  isUnlocking = false,
}) => {
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [sessionData, setSessionData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [movements, setMovements] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [ordersById, setOrdersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    netCollected: 0,
    totalCreditSales: 0,
    reserveRedeemedMinor: 0,
    theoreticalBalance: 0,
    openOrdersValue: 0,
    openOrdersCount: 0,
  });
  const [creditSaleEnabled, setCreditSaleEnabled] = useState(false);
  const [adminCanDeleteSales, setAdminCanDeleteSales] = useState(false);
  const [voidSale, setVoidSale] = useState(null);
  const [showVoidHistory, setShowVoidHistory] = useState(false);
  const [reserveEnabled, setReserveEnabled] = useState(false);
  const [reserveTicket, setReserveTicket] = useState(null);
  useEffect(() => {
    if (!isOpen) return;
    let current = true;
    apiFetch('/api/reserves/settings', { includeBearer: true }).then(async response => {
      if (!response.ok) return;
      const settings = await response.json();
      if (current) setReserveEnabled(Boolean(settings.enabled && settings.ready && !settings.consultationOnly));
    }).catch(() => { if (current) setReserveEnabled(false); });
    return () => { current = false; };
  }, [isOpen]);

  const parseBooleanFlag = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
    return fallback;
  };

  useEffect(() => {
    if (!isOpen) return;
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (_error) {
        // ignore and fallback to cache
      }
      try {
        const cached = localStorage.getItem("restaurant-info");
        if (cached) {
          const parsed = JSON.parse(cached);
          setBusinessType(extractBusinessType(parsed));
        }
      } catch (_error) {
        // ignore
      }
    };
    loadBusinessType();
  }, [isOpen]);

  // Nettoyer/masquer les notes automatiques générées côté serveur
  const sanitizeNotes = (notes) => {
    if (!notes) return "";
    const n = String(notes).trim();
    if (!n) return "";
    const lower = n.toLowerCase();
    const looksAuto =
      lower.startsWith("paiement ") ||
      lower.includes(" table ") ||
      lower.includes("table:") ||
      lower.includes("commande(s)") ||
      lower.includes("caissier:");
    return looksAuto ? "" : n;
  };

  const getUserId = (user) => {
    const raw =
      user?.id ??
      user?.userId ??
      user?.user_id ??
      user?.uid ??
      user?.cashier_id ??
      null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const normalizeTextForCompare = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ");

  // État pour le modal des mouvements de caisse
  const [showCashMovementsModal, setShowCashMovementsModal] = useState(false);

  // État pour le modal de détails de commande (désactivé pour le moment)
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState('');
  const orderDetailsRequest = useRef(0);
  const [isReprinting, setIsReprinting] = useState(false);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const journalScrollContainerRef = useRef(null);
  const [journalScrollTop, setJournalScrollTop] = useState(0);
  const [journalViewportHeight, setJournalViewportHeight] = useState(0);
  const JOURNAL_ROW_HEIGHT = 72;
  const JOURNAL_OVERSCAN = 6;

  // Calculs de pagination mémorisés pour éviter les bugs
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(transactions.length / itemsPerPage);

    // Vérifier que la page courante est valide
    let validCurrentPage = currentPage;
    if (totalPages > 0 && currentPage > totalPages) {
      validCurrentPage = totalPages;
    }
    if (validCurrentPage < 1) {
      validCurrentPage = 1;
    }

    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTransactions = transactions.slice(startIndex, endIndex);

    console.log("🔄 Pagination recalculée:", {
      totalTransactions: transactions.length,
      requestedPage: currentPage,
      validCurrentPage,
      totalPages,
      startIndex,
      endIndex,
      currentTransactionsCount: currentTransactions.length,
    });

    // Si la page courante n'est pas valide, la corriger
    if (validCurrentPage !== currentPage && totalPages > 0) {
      setTimeout(() => setCurrentPage(validCurrentPage), 0);
    }

    return {
      totalPages,
      currentPage: validCurrentPage,
      startIndex,
      endIndex,
      currentTransactions,
    };
  }, [transactions, currentPage, itemsPerPage]);

  const { totalPages, startIndex, endIndex, currentTransactions } =
    paginationData;
  const tableColSpan = isBoutique ? 9 : 10;

  const virtualizedTransactions = useMemo(() => {
    const allRows = Array.isArray(currentTransactions) ? currentTransactions : [];
    if (!allRows.length) {
      return {
        rows: [],
        topPadding: 0,
        bottomPadding: 0,
      };
    }

    const viewport = Math.max(journalViewportHeight || 0, JOURNAL_ROW_HEIGHT * 5);
    const visibleCount = Math.ceil(viewport / JOURNAL_ROW_HEIGHT);
    const baseStart = Math.floor(Math.max(journalScrollTop, 0) / JOURNAL_ROW_HEIGHT);
    const start = Math.max(0, baseStart - JOURNAL_OVERSCAN);
    const end = Math.min(
      allRows.length,
      baseStart + visibleCount + JOURNAL_OVERSCAN,
    );

    return {
      rows: allRows.slice(start, end),
      topPadding: start * JOURNAL_ROW_HEIGHT,
      bottomPadding: Math.max(0, (allRows.length - end) * JOURNAL_ROW_HEIGHT),
    };
  }, [currentTransactions, journalScrollTop, journalViewportHeight]);

  // Fonctions de navigation
  const goToPreviousPage = () => {
    setCurrentPage((prev) => {
      const newPage = Math.max(prev - 1, 1);
      console.log("⬅️ Page précédente:", prev, "->", newPage);
      return newPage;
    });
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => {
      const newPage = Math.min(prev + 1, totalPages);
      console.log(
        "➡️ Page suivante:",
        prev,
        "->",
        newPage,
        "(totalPages:",
        totalPages,
        ")",
      );
      return newPage;
    });
  };

  // Réinitialiser la page lors du changement de session
  useEffect(() => {
    setCurrentPage(1);
  }, [currentSession?.id]);

  useEffect(() => {
    setJournalScrollTop(0);
    const node = journalScrollContainerRef.current;
    if (node) {
      node.scrollTop = 0;
    }
  }, [currentPage, currentSession?.id, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const node = journalScrollContainerRef.current;
    const syncViewport = () => {
      const nextHeight = node?.clientHeight || 0;
      if (nextHeight !== journalViewportHeight) {
        setJournalViewportHeight(nextHeight);
      }
    };
    syncViewport();
    window.addEventListener("resize", syncViewport);
    return () => window.removeEventListener("resize", syncViewport);
  }, [isOpen, journalViewportHeight, currentPage]);

  // Vérifier que la page courante ne dépasse pas le nombre total de pages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Charger les moyens de paiement configurés
  const loadPaymentMethods = async () => {
    try {
      console.log("🔄 Chargement des moyens de paiement pour le journal...");
      const response = await fetch(`${API_BASE}/payment-methods/all`);
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Moyens de paiement chargés pour journal:", data);

        // Transformer les données avec les icônes et couleurs
        const formattedMethods = data.map((method) => ({
          code: method.code,
          name: method.name,
          icon: PAYMENT_ICONS[method.icon_name] || Banknote,
          color: method.color,
          description: method.description,
        }));

        setPaymentMethods(formattedMethods);
      } else {
        console.error(
          "❌ Erreur chargement moyens de paiement journal:",
          response.status,
        );
        // Fallback vers les moyens par défaut
        setPaymentMethods([
          { code: "cash", name: "Espèces", icon: Banknote, color: "#10B981" },
          {
            code: "card",
            name: "Carte bancaire",
            icon: CreditCard,
            color: "#3B82F6",
          },
          {
            code: "mobile",
            name: "Mobile Money",
            icon: Smartphone,
            color: "#F59E0B",
          },
        ]);
      }
    } catch (error) {
      console.error("❌ Erreur API moyens de paiement journal:", error);
      // Fallback vers les moyens par défaut
      setPaymentMethods([
        { code: "cash", name: "Espèces", icon: Banknote, color: "#10B981" },
        {
          code: "card",
          name: "Carte bancaire",
          icon: CreditCard,
          color: "#3B82F6",
        },
        {
          code: "mobile",
          name: "Mobile Money",
          icon: Smartphone,
          color: "#F59E0B",
        },
      ]);
    }
  };

  // Charger toutes les commandes pour récupérer les payment_number
  const loadOrdersIndex = async () => {
    try {
      const response = await fetch(`${API_BASE}/orders`);
      if (response.ok) {
        const orders = await response.json();
        const map = {};
        (orders || []).forEach((o) => {
          if (!o || o.id == null) return;
          map[o.id] = o;
        });
        setOrdersById(map);
        console.log(`✅ Index commandes chargé: ${Object.keys(map).length}`);
        return map;
      }
    } catch (e) {
      console.log("⚠️ Impossible de charger l'index des commandes", e.message);
    }
    return null;
  };

  const getAllOrdersCached = async () => {
    const existingOrders = Object.values(ordersById || {});
    if (existingOrders.length > 0) return existingOrders;
    const map = await loadOrdersIndex();
    return Object.values(map || {});
  };

  // Extraire les métadonnées utiles pour trier correctement les transactions
  const getTransactionSortMetadata = (transaction) => {
    const parsedDate = new Date(
      transaction.payment_created_at ||
        transaction.created_at ||
        transaction.order_updated_at,
    );
    const timestamp = isNaN(parsedDate.getTime())
      ? Date.now()
      : parsedDate.getTime();
    const entryId =
      parseInt(transaction.payment_entry_id, 10) ||
      parseInt(transaction.order_id, 10) ||
      0;

    return {
      timestamp,
      entryId,
    };
  };

  // Garantir un tri chronologique fiable (date + partie séquentielle)
  const sortTransactionsChronologically = (transactionList) => {
    return [...transactionList].sort((a, b) => {
      const aKey = getTransactionSortMetadata(a);
      const bKey = getTransactionSortMetadata(b);

      if (aKey.timestamp !== bKey.timestamp) {
        return aKey.timestamp - bKey.timestamp;
      }

      return aKey.entryId - bKey.entryId;
    });
  };

  // Génére un numéro de commande cohérent si payment_number est manquant
  const buildFallbackCommandeId = (transaction) => {
    const date = new Date(
      transaction.payment_created_at ||
        transaction.created_at ||
        transaction.order_updated_at ||
        Date.now(),
    );
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    const sequence = (transaction.order_id || transaction.id || "1").toString();

    return `#${day}${month}${year}${sequence}`;
  };

  // Charger les données de la session
  const loadSessionData = async () => {
    if (!currentSession || !currentUser) {
      console.log("⚠️ Session ou utilisateur manquant:", {
        currentSession,
        currentUser,
      });
      return;
    }

    setLoading(true);
    try {
      console.log("🔄 Chargement des données pour session:", currentSession.id);

      const creditEnabled = await loadCreditSaleSetting();
      await loadAdminOptions();

      // Charger toutes les données en parallèle avec les bonnes routes API
      await Promise.all([
        loadPaymentMethods(),
        loadRestaurantInfo(),
        loadSessionInfo(),
        loadSessionTransactions(),
        loadSessionMovements(),
        calculateSummary(creditEnabled),
      ]);

      console.log("✅ Toutes les données chargées avec succès");
    } catch (error) {
      console.error("❌ Erreur chargement données session:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminOptions = async () => {
    try {
      const response = await fetch(`${API_BASE}/settings/admin-options?_=${Date.now()}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        setAdminCanDeleteSales(false);
        return;
      }
      const data = await response.json().catch(() => ({}));
      setAdminCanDeleteSales(
        parseBooleanFlag(data?.admin_can_delete_sales, false),
      );
    } catch (_error) {
      setAdminCanDeleteSales(false);
    }
  };

  const loadCreditSaleSetting = async () => {
    try {
      const response = await fetch(`${API_BASE}/settings/credit-sale`, {
        cache: "no-store",
      });
      if (!response.ok) {
        setCreditSaleEnabled(false);
        return false;
      }
      const data = await response.json().catch(() => ({}));
      const enabled = parseBooleanFlag(data?.enabled, false);
      setCreditSaleEnabled(enabled);
      return enabled;
    } catch (_error) {
      setCreditSaleEnabled(false);
      return false;
    }
  };

  // Charger les informations de la session
  const loadSessionInfo = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/cash-journal/session/${currentSession.id}`,
      );
      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
        console.log("✅ Informations session chargées:", data);
      } else {
        console.error("❌ Erreur chargement info session:", response.status);
      }
    } catch (error) {
      console.error("❌ Erreur API info session:", error);
    }
  };

  const loadRestaurantInfo = async () => {
    try {
      const response = await fetch(`${API_BASE}/restaurant-info`);
      if (!response.ok) return;
      const data = await response.json().catch(() => null);
      if (data) setRestaurantInfo(data);
    } catch (error) {
      console.error("❌ Erreur chargement restaurant-info:", error);
    }
  };

  // Charger les transactions (ventes) de la session courante UNIQUEMENT
  const loadSessionTransactions = async () => {
    try {
      console.log(
        `🔄 Chargement transactions de la session ${currentSession.id} (vue session strict)`,
      );

      const cashierId = getUserId(currentUser) || getUserId(currentSession);
      const cashierQuery = cashierId ? `?cashierId=${cashierId}` : "";
      const response = await fetch(
        `${API_BASE}/cash-journal/transactions/${currentSession.id}${cashierQuery}`,
      );
      if (response.ok) {
        const transactionDataRaw = await response.json();
        const transactionData = Array.isArray(transactionDataRaw)
          ? transactionDataRaw
          : [];
        console.log("✅ Transactions de session récupérées:", transactionData.length);

        // Sécurité UI : le journal de caisse est personnel -> ne garder que les transactions du caissier connecté
        const expectedCashierId = cashierId;
        const expectedCashierName = normalizeTextForCompare(
          [currentUser?.first_name, currentUser?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim(),
        );
        const filteredTransactionData = transactionData.filter((tx) => {
          if (expectedCashierId) {
            const txCashierId = getUserId(tx);
            if (txCashierId) return txCashierId === expectedCashierId;
          }
          if (expectedCashierName) {
            const txName = normalizeTextForCompare(
              tx.cashier_name || tx.cashierName,
            );
            if (txName) return txName === expectedCashierName;
          }
          return true;
        });

        const sortedTransactions = sortTransactionsChronologically(
          filteredTransactionData,
        );

	        const formattedTransactions = sortedTransactions.map((tx) => {
	          const totalAmount = parseFloat(tx.total_amount || 0) || 0;
	          const changeGiven = parseFloat(tx.change_given || 0) || 0;
	          const amountReceived =
	            tx.amount_received != null
	              ? parseFloat(tx.amount_received)
	              : totalAmount + changeGiven;
	          const normalizedMethodCode = (tx.payment_method || "").toString().trim().toLowerCase();
	          const isCreditPayment =
	            normalizedMethodCode === "credit" ||
	            normalizedMethodCode === "a-credit" ||
	            normalizedMethodCode === "à-crédit";
	          const netPaidAmount = isCreditPayment
	            ? totalAmount
	            : Math.max(0, amountReceived - changeGiven);
	          const reserveRedeemedAmount =
	            Math.max(0, Number(tx.reserve_redeemed_minor) || 0) / 100;
	          const isDelivery = !!tx.is_delivery;
	          const paymentTimeRaw =
	            tx.payment_created_at || tx.created_at || tx.order_updated_at;
	          const paymentTime = paymentTimeRaw
	            ? new Date(paymentTimeRaw)
            : new Date();
          let deliveryCustomerData = null;
          if (tx.delivery_customer_data) {
            try {
              deliveryCustomerData = JSON.parse(tx.delivery_customer_data);
            } catch (_) {
              deliveryCustomerData = null;
            }
          }
          const deliveryReference =
            deliveryCustomerData?.delivery_reference ||
            deliveryCustomerData?.reference ||
            deliveryCustomerData?.code ||
            null;
          const deliveryMethodLabel = tx.delivery_method
            ? `${tx.delivery_method}`.charAt(0).toUpperCase() +
              `${tx.delivery_method}`.slice(1)
            : isDelivery
              ? "Direct"
              : null;
          const deliveryModeLabel =
            tx.delivery_mode === "takeaway"
              ? "À emporter"
              : tx.delivery_mode === "delivery"
                ? "Livraison"
                : null;
          const directCustomerName =
            deliveryCustomerData?.name ||
            [deliveryCustomerData?.first_name, deliveryCustomerData?.last_name]
              .filter((part) => part && part.trim())
              .join(" ") ||
            tx.customer_name ||
            "";
          const directCustomerPhone =
            deliveryCustomerData?.phone ||
            deliveryCustomerData?.contact ||
            tx.customer_phone ||
            "";
          const isDirectDelivery =
            isDelivery &&
            (!deliveryMethodLabel ||
              deliveryMethodLabel.toLowerCase() === "direct");
          const deliveryReferenceDisplay = isDirectDelivery
            ? [directCustomerName, directCustomerPhone]
                .filter((val) => val && `${val}`.trim())
                .join(" / ")
            : deliveryReference;
          const tableNumberRaw = tx.table_number || "N/A";
          const isCounter =
            !isDelivery &&
            String(tx.order_type || "").trim().toLowerCase() === "takeaway";
          const counterAreaName = String(tx.origin_zone_name || "").trim();
          const tableDisplay = isDelivery
            ? "Livraison"
            : isCounter
              ? `Comptoir${counterAreaName ? ` · ${counterAreaName}` : ""}`
              : tableNumberRaw;

          // Utiliser en priorité les notes du paiement (lp.notes) qui contiennent
          // le tag technique MOO_BREAKDOWN pour les paiements multiples.
          const rawNotes = tx.payment_notes || tx.notes || "";
          const rawPaymentBreakdown =
            parsePaymentBreakdownFromNotes(rawNotes) || null;
          const netExternalBreakdown = rawPaymentBreakdown
            ? buildNetPaymentBreakdown(
                rawPaymentBreakdown,
                changeGiven,
                paymentMethods,
              )
            : null;
          const paymentBreakdown = buildJournalPaymentBreakdown({
            entries: netExternalBreakdown || [],
            paymentMethod: tx.payment_method || "cash",
            collectedAmount: netPaidAmount,
            reserveRedeemedAmount,
          });

          const resolvedCommandeId = tx.payment_number || "";
          const commandeId =
            resolvedCommandeId && resolvedCommandeId.trim().length > 0
              ? resolvedCommandeId
              : buildFallbackCommandeId({
                  ...tx,
                  created_at: paymentTimeRaw || tx.created_at,
                });
          const groupedCommandeIds = `${tx.payment_numbers || ""}`
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);
          const groupedInternalOrderIds = `${tx.order_numbers || ""}`
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);

          return {
            id: `${tx.order_id}`,
            order_id: Number(tx.order_id) || null,
            cashEntryId: tx.group_id ?? null,
            time: paymentTime,
            tableNumber: tableDisplay,
            tableNumberRaw,
            commandeId,
            realCommandeId: tx.payment_number || null,
            paymentReference: tx.payment_reference || null,
            groupedCommandeIds,
            groupedInternalOrderIds,
            amount: totalAmount,
            collectedAmount: netPaidAmount,
            reserveRedeemedAmount,
            reserve: parseReserveProof(tx.reserve_proof_json),
            amountReceived,
            change: changeGiven,
            discountAmount: toNumberOrZero(tx.discount_amount),
            paymentMethod: tx.payment_method || "cash",
            paymentBreakdown,
            cashierName:
              tx.cashier_name ||
              (currentUser
                ? `${currentUser.first_name} ${currentUser.last_name}`
                : "Caissier"),
            // Les notes visibles doivent rester propres, sans le tag technique.
            notes: sanitizeNotes(rawNotes),
            isDelivery,
            paymentCreatedAt: paymentTimeRaw || null,
            paymentEntryId: tx.payment_entry_id || null,
            settlementId: tx.settlement_id || null,
            settlementKind: tx.settlement_kind || null,
            customerName: tx.customer_name || "",
            customerPhone: tx.customer_phone || "",
            deliveryMode: tx.delivery_mode || null,
            deliveryModeLabel,
            deliveryMethod: tx.delivery_method || null,
            deliveryMethodLabel,
            deliveryReference,
            deliveryReferenceDisplay: deliveryReferenceDisplay || "",
            directCustomerName,
            directCustomerPhone,
            originTerminalId: tx.origin_terminal_id || null,
            originZoneId: Number(tx.origin_zone_id) || null,
            originZoneName: counterAreaName || "",
          };
        });

        // Regrouper les livraisons et dine-in d'un même service dans une seule ligne
        const isDeliveryCommande = (id) => typeof id === "string" && id.includes("-");
        const parsePaymentParts = (id) => {
          if (!isDeliveryCommande(id)) return null;
          const m = id.match(/^(#\d{6})(\d+)-(\w+)/); // #ddmmyy{seq}-{suffix}
          if (!m) return null;
          return { datePrefix: m[1], seq: parseInt(m[2], 10), suffix: m[3] };
        };
        const parseDineInParts = (id) => {
          if (typeof id !== "string" || !id.startsWith("#")) return null;
          const m = id.match(/^#(\d{6})(\d+)$/); // #ddmmyy{seq}
          if (!m) return null;
          return { datePrefix: `#${m[1]}`, seq: parseInt(m[2], 10) };
        };

        const byGroup = [];
        const thresholdMs = 5 * 60 * 1000; // 5 minutes
        const orderedTransactions = [...formattedTransactions].sort((a, b) => b.time - a.time);

        const isDeliveryPlaceholder = (entry) =>
          (!entry.commandeId || entry.commandeId === "") &&
          (!entry.tableNumber || `${entry.tableNumber}`.toUpperCase() === "N/A");

        const sameCashierAndMethod = (a, b) =>
          (a.paymentMethod || "") === (b.paymentMethod || "") &&
          (a.cashierName || "") === (b.cashierName || "");

        const getDeliverySuffix = (commandeId) => {
          const parsed = parsePaymentParts(commandeId);
          return parsed ? parsed.suffix : null;
        };

        for (const tx of orderedTransactions) {
          const parts = parsePaymentParts(tx.commandeId);
          const deliverySuffix = parts?.suffix || null;
          const placeholderDelivery = isDeliveryPlaceholder(tx);
          const isNumericTable = tx.tableNumber && /^\d+$/.test(`${tx.tableNumber}`);
          let idx = -1;

          if (deliverySuffix) {
            // Ne pas fusionner deux vraies commandes livraison : chaque encaissement doit rester visible.
            idx = -1;
          } else if (placeholderDelivery) {
            // Placeholder arrivé avant/ après la vraie ligne livraison
            idx = byGroup.findIndex((g) => {
              const suffix = g.__deliverySuffix || getDeliverySuffix(g.commandeId);
              if (!suffix) return false;
              if (!sameCashierAndMethod(g, tx)) return false;
              return Math.abs(g.time - tx.time) <= thresholdMs;
            });

            if (idx === -1) {
              // Dernier recours: fusionner avec un autre placeholder déjà enregistré
              idx = byGroup.findIndex((g) => {
                if (!g.__isDeliveryPlaceholder) return false;
                if (!sameCashierAndMethod(g, tx)) return false;
                return Math.abs(g.time - tx.time) <= thresholdMs;
              });
            }
          } else if (isNumericTable) {
            idx = -1;
          }

          if (idx === -1) {
            byGroup.push({
              ...tx,
              __deliverySuffix: deliverySuffix || null,
              __isDeliveryPlaceholder: placeholderDelivery,
            });
          } else {
            const g = byGroup[idx];
            // Additionner montants
            g.amount = (g.amount || 0) + (tx.amount || 0);
            g.collectedAmount =
              (g.collectedAmount || 0) + (tx.collectedAmount || 0);
            g.reserveRedeemedAmount =
              (g.reserveRedeemedAmount || 0) + (tx.reserveRedeemedAmount || 0);
            g.amountReceived = (g.amountReceived || 0) + (tx.amountReceived || 0);
            g.change = (g.change || 0) + (tx.change || 0);
            g.paymentBreakdown = mergeJournalPaymentBreakdown([
              ...(Array.isArray(g.paymentBreakdown) ? g.paymentBreakdown : []),
              ...(Array.isArray(tx.paymentBreakdown) ? tx.paymentBreakdown : []),
            ]);
            if (!g.reserve && tx.reserve) g.reserve = tx.reserve;

            // Garder la commande la plus ancienne pour l'affichage
            const gpDel = parsePaymentParts(g.commandeId);
            const tpDel = parsePaymentParts(tx.commandeId);
            if (gpDel && tpDel && gpDel.seq > tpDel.seq) {
              g.commandeId = tx.commandeId;
            }
            const gpDi = parseDineInParts(g.commandeId);
            const tpDi = parseDineInParts(tx.commandeId);
            if (!gpDel && !tpDel && gpDi && tpDi && gpDi.seq > tpDi.seq) {
              g.commandeId = tx.commandeId;
            }

            if ((!g.commandeId || g.commandeId === "") && tx.commandeId) {
              g.commandeId = tx.commandeId;
            }

            if (deliverySuffix && !g.__deliverySuffix) {
              g.__deliverySuffix = deliverySuffix;
            }
            if (placeholderDelivery) {
              g.__isDeliveryPlaceholder = true;
            }

            // Fusionner les notes si l'une est vide
            if (!g.notes && tx.notes) g.notes = tx.notes;
          }
        }

        const finalTransactions = byGroup
          .map((entry) => {
            const { __deliverySuffix, __isDeliveryPlaceholder, ...publicEntry } = entry;
            const computedId =
              publicEntry.commandeId && publicEntry.commandeId.trim().length > 0
                ? publicEntry.commandeId
                : buildFallbackCommandeId({
                    created_at: publicEntry.time,
                    order_id: publicEntry.id,
                    id: publicEntry.id,
                  });

            return {
              ...publicEntry,
              commandeId: computedId,
            };
          })
          .sort((a, b) => {
            const timeDiff = b.time - a.time;
            if (timeDiff !== 0) return timeDiff;
            const aEntryId = Number(a.paymentEntryId || a.id) || 0;
            const bEntryId = Number(b.paymentEntryId || b.id) || 0;
            if (aEntryId !== bEntryId) return bEntryId - aEntryId;
            return String(b.commandeId || "").localeCompare(
              String(a.commandeId || ""),
            );
          });

        const totalRevenueFromTransactions = finalTransactions.reduce(
          (sum, tx) => sum + (tx.amount || 0),
          0,
        );

        setTransactions(finalTransactions);
        setSummary((prev) => ({
          ...prev,
          totalOrders: finalTransactions.length,
          totalRevenue:
            totalRevenueFromTransactions > 0
              ? totalRevenueFromTransactions
              : prev?.totalRevenue || 0,
        }));
      } else {
        console.error(
          "❌ Erreur réponse transactions de session:",
          response.status,
        );
        setTransactions([]);
      }
    } catch (error) {
      console.error("❌ Erreur chargement transactions de session:", error);
      setTransactions([]);
    }
  };

  // Charger les mouvements de caisse de la session
  const loadSessionMovements = async () => {
    try {
      console.log(`🔄 Chargement mouvements pour session ${currentSession.id}`);

      const response = await fetch(
        `${API_BASE}/cash-movements/session/${currentSession.id}`,
      );
      if (response.ok) {
        const movementsData = await response.json();
        setMovements(movementsData);
        console.log("✅ Mouvements récupérés:", movementsData.length);
      } else {
        console.error("❌ Erreur réponse mouvements:", response.status);
        setMovements([]);
      }
    } catch (error) {
      console.error("❌ Erreur chargement mouvements:", error);
      setMovements([]);
    }
  };

  // Calculer le résumé avec la bonne route API
  // Calculer le résumé de la session
  const calculateSummary = async (creditEnabledOverride) => {
    try {
      console.log(
        `🔄 Calcul résumé pour session ${currentSession.id} (isUnlocking: ${isUnlocking})`,
      );
      const isCreditEnabled =
        typeof creditEnabledOverride === "boolean"
          ? creditEnabledOverride
          : creditSaleEnabled;

      // Utiliser la route appropriée selon le type de connexion
      const cashierId = getUserId(currentUser) || getUserId(currentSession);
      const cashierQuery = cashierId ? `?cashierId=${cashierId}` : "";
      const apiEndpoint = isUnlocking
        ? `${API_BASE}/cash-journal/summary-unlock/${currentSession.id}${cashierQuery}`
        : `${API_BASE}/cash-journal/summary/${currentSession.id}${cashierQuery}`;

      console.log(`📡 Route API résumé utilisée: ${apiEndpoint}`);

      const response = await fetch(apiEndpoint);
      if (response.ok) {
        const summaryData = await response.json();
        console.log("✅ Résumé reçu:", summaryData);

        const invoicedTotal = Number(
          summaryData.summary?.invoiced_total ??
            summaryData.summary?.total_revenue ??
            0,
        );
        const creditTotal = isCreditEnabled
          ? Number(summaryData.summary?.total_credit_sales) || 0
          : 0;
        const redeemedMinor =
          Number(summaryData.summary?.reserves?.redeemedMinor) || 0;
        setSummary({
          totalOrders: summaryData.summary?.total_orders || 0,
          totalRevenue: invoicedTotal,
          netCollected: Math.max(0, invoicedTotal - creditTotal - redeemedMinor / 100),
          totalCreditSales: creditTotal,
          reserveRedeemedMinor: redeemedMinor,
          theoreticalBalance: summaryData.summary?.theoretical_balance || 0,
          openOrdersValue: summaryData.open_orders?.value || 0,
          openOrdersCount: summaryData.open_orders?.count || 0,
        });
      } else {
        console.error("❌ Erreur réponse résumé:", response.status);
        // Calcul de fallback
        await calculateFallbackSummary();
      }
    } catch (error) {
      console.error("❌ Erreur calcul résumé:", error);
      await calculateFallbackSummary();
    }
  };

  // Calcul de résumé de secours
  const calculateFallbackSummary = async () => {
    try {
      const totalOrders = transactions.length;
      const totalRevenue = transactions.reduce((sum, t) => sum + t.amount, 0);
      const totalCreditSales = transactions
        .filter((t) =>
          ["credit", "a-credit", "à-crédit"].includes(
            (t.paymentMethod || "").toString().trim().toLowerCase(),
          ),
        )
        .reduce((sum, t) => sum + t.amount, 0);
      const effectiveCreditSales = creditSaleEnabled ? totalCreditSales : 0;

      const totalDeposits = movements
        .filter((m) => m.type === "deposit")
        .reduce((sum, m) => sum + parseFloat(m.amount), 0);

      const totalWithdrawals = movements
        .filter((m) => m.type === "withdrawal")
        .reduce((sum, m) => sum + parseFloat(m.amount), 0);

      const initialAmount = parseFloat(currentSession.initial_amount || 0);
      const reserveResponse = await fetch(
        `${API_BASE}/reserves/activity?sessionId=${encodeURIComponent(currentSession.id)}`,
      );
      if (!reserveResponse.ok) {
        throw new Error("Réserves utilisées indisponibles pour le calcul du solde");
      }
      const reserveActivity = await reserveResponse.json();
      const reserveRedeemedMinor = Number(reserveActivity?.redeemedMinor) || 0;
      const theoreticalBalance =
        initialAmount +
        totalDeposits -
        totalWithdrawals +
        (totalRevenue - effectiveCreditSales - reserveRedeemedMinor / 100);

      setSummary((prev) => ({
        totalOrders,
        totalRevenue,
        netCollected: Math.max(
          0,
          totalRevenue - effectiveCreditSales - reserveRedeemedMinor / 100,
        ),
        totalCreditSales: effectiveCreditSales,
        reserveRedeemedMinor,
        theoreticalBalance,
        openOrdersValue: prev?.openOrdersValue || 0,
        openOrdersCount: prev?.openOrdersCount || 0,
      }));

      console.log("✅ Résumé calculé en fallback:", {
        totalOrders,
        totalRevenue,
        totalCreditSales: effectiveCreditSales,
        reserveRedeemedMinor,
        theoreticalBalance,
      });
    } catch (error) {
      console.error("❌ Erreur calcul fallback:", error);
    }
  };

  // Générer l'ID de commande selon le nouveau format spécifié
  const generateCommandeId = (order, payment) => {
    // Si payment_number existe déjà, l'utiliser
    if (order.payment_number) {
      return order.payment_number;
    }

    // Sinon, générer selon le nouveau format #{jjmmaaaa}{n_paiement}
    const date = new Date(payment.created_at);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();

    // Utiliser l'ID de commande comme numéro séquentiel temporaire
    const orderNumber = order.id;

    return `#${day}${month}${year}${orderNumber}`;
  };

  // Formatter la devise
  const formatCurrency = (amount) => {
    // Montants déjà en FCFA, pas de conversion nécessaire
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatMoneyAscii = (amount) =>
    String(Math.round(toNumberOrZero(amount))).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const shortPersonName = (value) => {
    const s = String(value || "").trim();
    if (!s) return "";
    return s.split(/\s+/)[0] || s;
  };

  const getUserShortLabel = (user) => {
    const firstName = String(user?.first_name || user?.firstName || "").trim();
    if (firstName) return firstName;
    const username = String(user?.username || "").trim();
    if (username) return username;
    const name = String(user?.name || "").trim();
    if (name) return shortPersonName(name);
    return "Caissier";
  };

  const fetchReceiptPrinterSettingsSafe = async () => {
    try {
      const res = await fetch(`${API_BASE}/receipt-printer-settings`);
      if (!res.ok) return null;
      return (await res.json()) || null;
    } catch (_) {
      return null;
    }
  };

  const resolveUsbPrintOptions = (settings = {}) => ({
    silent: true,
    paperWidthMm: Number(settings?.paper_width_mm) === 58 ? 58 : 80,
    copies: Math.max(1, Math.floor(Number(settings?.copies) || 1)),
    deviceName:
      settings?.printer_name &&
      String(settings.printer_name).trim().toLowerCase() !== "ticket caisse"
        ? String(settings.printer_name).trim()
        : undefined,
  });

  const printHtmlInHiddenIframe = (html, options = {}) =>
    new Promise((resolve, reject) => {
      try {
        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.left = "-10000px";
        iframe.style.top = "0";
        iframe.style.width = "820px";
        iframe.style.height = "1200px";
        iframe.style.border = "0";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
        iframe.setAttribute("aria-hidden", "true");

        let settled = false;
        let fallbackTimer = null;
        const cleanup = () => {
          if (settled) return;
          settled = true;
          if (fallbackTimer) window.clearTimeout(fallbackTimer);
          try {
            iframe.remove();
          } catch (_) {}
          resolve(true);
        };

        iframe.onload = async () => {
          try {
            const w = iframe.contentWindow;
            if (!w) throw new Error("Fenêtre d'impression indisponible.");
            try {
              if (w.document?.fonts?.ready) await w.document.fonts.ready;
            } catch (_) {}
            await new Promise((ready) =>
              w.requestAnimationFrame(() => w.requestAnimationFrame(ready)),
            );
            const body = w.document.body;
            const bodyRect = body.getBoundingClientRect();
            const contentHeightPx = Math.max(
              1,
              ...Array.from(body.children || []).map((child) => {
                const rect = child.getBoundingClientRect();
                return rect.bottom - bodyRect.top;
              }),
            );
            const page = calculateThermalPageSize({
              contentHeightPx,
              paperWidthMm: options.paperWidthMm || 80,
            });
            const style = w.document.createElement("style");
            style.textContent = `@page { size: ${page.widthMm}mm ${page.heightMm}mm; margin: 0 !important; }`;
            w.document.head.appendChild(style);
            w.addEventListener("afterprint", cleanup, { once: true });
            fallbackTimer = window.setTimeout(cleanup, 60000);
            w.focus();
            w.print();
          } catch (err) {
            if (!settled) {
              settled = true;
              if (fallbackTimer) window.clearTimeout(fallbackTimer);
              try {
                iframe.remove();
              } catch (_) {}
              reject(err);
            }
          }
        };

        iframe.srcdoc = html;
        document.body.appendChild(iframe);
      } catch (err) {
        reject(err);
      }
    });

  const printHtmlViaUsbPrinter = async (html, settings = {}) => {
    const options = resolveUsbPrintOptions(settings);
    if (window?.electronAPI?.printHtml) {
      await window.electronAPI.printHtml({ html, ...options });
      return;
    }
    await printHtmlInHiddenIframe(html, options);
  };

  const toNumberOrZero = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const computeExtrasTotal = (extras) => {
    if (!Array.isArray(extras) || extras.length === 0) return 0;
    return extras.reduce((sum, extra) => {
      const quantity = toNumberOrZero(extra?.quantity) || 1;
      const unitPrice = toNumberOrZero(extra?.unit_price ?? extra?.price ?? 0);
      const totalPrice =
        extra?.total_price != null ? toNumberOrZero(extra.total_price) : unitPrice * quantity;
      return sum + totalPrice;
    }, 0);
  };

  const computeItemsInvoiceTotal = (items) => {
    if (!Array.isArray(items) || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const quantity = toNumberOrZero(item?.quantity ?? item?.qty) || 1;

      const totalWithExtras = toNumberOrZero(item?.total_price_with_extras);
      if (totalWithExtras > 0) return sum + totalWithExtras;

      const extrasTotal =
        toNumberOrZero(item?.extras_total) || computeExtrasTotal(item?.extras);
      const unitPrice = toNumberOrZero(
        item?.unit_price ?? item?.price ?? item?.product_price ?? item?.base_price ?? 0,
      );
      const baseTotal = toNumberOrZero(item?.total_price);
      const base = baseTotal > 0 ? baseTotal : unitPrice * quantity;

      return sum + base + extrasTotal;
    }, 0);
  };

	  const computeInvoiceTotal = (details, transaction) => {
	    const orderTotal = toNumberOrZero(
	      details?.order?.total_amount ?? details?.order?.total ?? details?.order?.amount,
	    );
	    if (orderTotal > 0) return orderTotal;

	    const itemsTotal = computeItemsInvoiceTotal(details?.items);
	    if (itemsTotal > 0) {
	      const discount = toNumberOrZero(details?.order?.discount_amount);
	      return Math.max(0, itemsTotal - Math.max(0, discount));
	    }

	    return toNumberOrZero(transaction?.amount);
	  };

  // Formatter l'heure
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const normalizeText = (value) => {
    if (value === null || value === undefined) return null;
    const stringValue = typeof value === "string" ? value : value.toString();
    const trimmed = stringValue.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  const parseVariationAttributesLabel = (raw) => {
    // Support JSON double-encodé et objets/arrays pour afficher correctement la variation
    if (!raw) return "";
    let parsed = raw;
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch (_) {
        return normalizeText(parsed) || "";
      }
    }
    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch (_) {
        return normalizeText(parsed) || "";
      }
    }
    if (Array.isArray(parsed)) {
      return parsed.map((v) => normalizeText(v)).filter(Boolean).join(" / ");
    }
    if (parsed && typeof parsed === "object") {
      return Object.values(parsed)
        .map((v) => normalizeText(v))
        .filter(Boolean)
        .join(" / ");
    }
    return "";
  };

  const formatVariationDisplay = (item = {}) => {
    const baseName =
      item.product_label ||
      item.productLabel ||
      item.product_display_name ||
      item.productDisplayName ||
      item.product_name ||
      item.productName ||
      item.name ||
      item.title ||
      item.display_name ||
      item.full_name ||
      "Article";

    const variationLabelRaw =
      item.variation_label ||
      item.variationLabel ||
      item.variation_name ||
      item.variationName ||
      item.variation_name_ref ||
      item.variationNameRef ||
      item.variant_name ||
      item.variantName ||
      item.variation_text ||
      "";
    const variationLabel = normalizeText(variationLabelRaw);

    const attrsRaw =
      item.variation_attributes ||
      item.variationAttributes ||
      item.variation_attributes_ref ||
      item.variationAttributesRef ||
      null;
    const attrText = parseVariationAttributesLabel(attrsRaw);

    const parts = [];
    if (attrText) parts.push(attrText);
    if (!attrText && variationLabel && !parts.includes(variationLabel)) {
      parts.push(variationLabel);
    }

    if (parts.length) return `${baseName} · ${parts.join(" · ")}`;
    return baseName;
  };
  const parsePaymentBreakdownFromNotes = (notes) => {
    if (!notes) return null;
    const marker = "MOO_BREAKDOWN=";
    const idx = notes.indexOf(marker);
    if (idx === -1) return null;
    const after = notes.slice(idx + marker.length).trim();
    const payload = after.split("|")[0].trim();
    if (!payload) return null;
    const parts = payload.split(",").map((entry) => entry.trim());
    const entries = parts
      .map((entry) => {
        const [id, rawAmount] = entry.split(":");
        if (!id) return null;
        const amount = parseFloat(rawAmount);
        return {
          methodId: id,
          amount: Number.isFinite(amount) ? amount : 0,
        };
      })
      .filter(Boolean);
    return entries.length ? entries : null;
  };

  // Enrichir le paiement d'une commande spécifique via l'endpoint de détails
  const enrichPaymentBreakdownFromOrderDetails = async (
    orderId,
    transactionData,
  ) => {
    if (transactionData?.settlementId || transactionData?.settlement_id) return;
    try {
      const response = await fetch(
        `${API_BASE}/orders/${orderId}/details`,
      );
      if (!response.ok) return;
      const data = await response.json();
      const order = data?.order;
      if (!order) return;

      const rawNotes =
        order.payment_notes || order.notes || transactionData?.notes || "";
      const breakdown =
        parsePaymentBreakdownFromNotes(rawNotes) || null;

      if (breakdown && breakdown.length) {
        const netBreakdown = buildNetPaymentBreakdown(
          breakdown,
          transactionData?.change,
          paymentMethods,
        );
        setSelectedOrder((prev) => {
          if (!prev || String(prev.id) !== String(orderId)) return prev;
          return {
            ...prev,
            paymentBreakdown: netBreakdown,
          };
        });
      }
    } catch (e) {
      console.log(
        "⚠️ Impossible de récupérer le détail des moyens de paiement:",
        e.message,
      );
    }
  };

  // Résoudre l'ID de commande à partir du numéro de paiement (payment_number)
  const resolveOrderIdFromPaymentNumber = async (
    orderId,
    transactionData = {},
  ) => {
    const paymentNumber =
      transactionData.realCommandeId ||
      transactionData.commandeId ||
      transactionData.payment_number ||
      transactionData.paymentNumber;

    // Si l'ID semble correct, on le garde mais on tentera quand même de matcher le payment_number
    let resolvedId =
      orderId && `${orderId}` !== "undefined" && `${orderId}` !== "null"
        ? orderId
        : null;

    // Dans le Journal de caisse, `tx.id` correspond déjà à `order_id` (id interne).
    // Éviter de recharger toute la liste `/orders` (payload lourd) si on a déjà un id exploitable.
    const hasUsableOrderId =
      resolvedId &&
      `${resolvedId}` !== "undefined" &&
      `${resolvedId}` !== "null" &&
      /^\d+$/.test(`${resolvedId}`.trim());
    if (hasUsableOrderId) return resolvedId;

    if (!paymentNumber) return resolvedId;

    try {
      const ordersRes = await fetch(`${API_BASE}/orders`, {
        cache: "no-store",
      });
      if (!ordersRes.ok) return resolvedId;
      const orders = await ordersRes.json();
      const cleanPaymentNumber = `${paymentNumber}`.replace("#", "").trim();
      const match = (orders || []).find((o) => {
        const pn = `${o.payment_number || ""}`.replace("#", "").trim();
        return pn === cleanPaymentNumber;
      });
      if (match?.id) {
        console.log(
          `🔎 OrderId résolu via payment_number ${paymentNumber}: ${match.id}`,
        );
        resolvedId = match.id;
      }
    } catch (e) {
      console.log("⚠️ Impossible de résoudre l'orderId via payment_number:", e);
    }

    return resolvedId || orderId;
  };

  // Récupère les articles d'une commande en conservant les variations/attributs
  const fetchOrderItemsWithVariations = async (orderId) => {
    // Tenter d'abord l'endpoint "sales" (déjà normalisé côté admin/sales)
    try {
      const salesRes = await fetch(`${API_BASE}/sales/${orderId}/details`, {
        cache: "no-store",
      });
      if (salesRes.ok) {
        const salesData = await salesRes.json();
        const salesItems = Array.isArray(salesData?.items) ? salesData.items : [];
        if (salesItems.length > 0) return salesItems;
      }
    } catch (e) {
      console.log(
        "⚠️ Impossible de charger les détails (sales) de la commande:",
        e.message,
      );
    }

    // Essayer en priorité l'endpoint complet qui contient les variations
    try {
      const detailsRes = await fetch(`${API_BASE}/orders/${orderId}/details`, {
        cache: "no-store",
      });
      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        const detailItems =
          detailsData?.items ||
          detailsData?.order?.items ||
          detailsData?.orderItems ||
          [];
        if (Array.isArray(detailItems) && detailItems.length > 0) {
          return detailItems;
        }
      }
    } catch (e) {
      console.log(
        "⚠️ Impossible de charger les détails complets de la commande:",
        e.message,
      );
    }

    // Fallback léger
    try {
      const itemsRes = await fetch(`${API_BASE}/orders/${orderId}/items`, {
        cache: "no-store",
      });
      if (itemsRes.ok) {
        const itemsData = await itemsRes.json();
        if (Array.isArray(itemsData?.items) && itemsData.items.length > 0) {
          return itemsData.items;
        }
      }
    } catch (e) {
      console.log(
        "⚠️ Impossible de charger les articles simples de la commande:",
        e.message,
      );
    }

    return [];
  };

  const fetchOrdersByCashEntryId = async (cashEntryId) => {
    if (cashEntryId === null || cashEntryId === undefined) return [];
    const encoded = encodeURIComponent(`${cashEntryId}`);

    try {
      const response = await fetch(
        `${API_BASE}/orders?cash_entry_id=${encoded}`,
        { cache: "no-store" },
      );
      if (response.ok) {
        const orders = await response.json();
        const list = Array.isArray(orders) ? orders : [];
        const target = `${cashEntryId}`;
        return list.filter((o) => o && `${o.cash_entry_id}` === target);
      }
    } catch (e) {
      console.log(
        "⚠️ Impossible de charger les commandes du cash_entry_id:",
        e.message,
      );
    }

    try {
      const allOrders = await getAllOrdersCached();
      const target = `${cashEntryId}`;
      return (allOrders || []).filter(
        (o) => o && `${o.cash_entry_id}` === target,
      );
    } catch (_) {
      return [];
    }
  };

  // Charger les détails complets d'une commande
  // eslint-disable-next-line no-unused-vars
  const loadOrderDetails = async (orderId, transactionData, verifiedOnly = false) => {
    const requestId = ++orderDetailsRequest.current;
    let loadedDetails = null;
    const publishDetails = value => {
      loadedDetails = value;
      if (requestId === orderDetailsRequest.current) setOrderDetails(value);
    };
    setLoadingOrderDetails(true);
    setOrderDetailsError('');
    setOrderDetails(null);
    try {
      const settlementDetails = await loadJournalSettlementDetails(transactionData);
      if (settlementDetails) { publishDetails(settlementDetails); return loadedDetails; }
      console.log(`🔄 Chargement des détails pour commande ${orderId}`);
      console.log("📋 Transaction data reçue:", transactionData);

      // Résoudre le bon orderId (si on a reçu un payment_number par exemple)
      const resolvedOrderId = await resolveOrderIdFromPaymentNumber(
        orderId,
        transactionData,
      );

      // ✅ Source la plus fiable côté journal: agréger toutes les commandes rattachées
      // à la même entrée de journal (cash_entry_id) afin que les articles concordent
      // avec le montant encaissé affiché.
      const cashEntryId =
        transactionData?.cashEntryId ??
        transactionData?.groupId ??
        transactionData?.cash_entry_id ??
        null;

      if (cashEntryId !== null && cashEntryId !== undefined) {
        try {
          const relatedOrders = await fetchOrdersByCashEntryId(cashEntryId);
          if (Array.isArray(relatedOrders) && relatedOrders.length > 0) {
            const aggregatedItems = [];
            const reserveProofs = [];
            let aggregatedInvoiceTotal = 0;
            let aggregatedDiscount = 0;

            for (const relatedOrder of relatedOrders) {
              const relatedOrderId = relatedOrder?.id;
              if (!relatedOrderId) continue;

              let relatedDetails = null;
              try {
                const detailsResponse = await fetch(
                  `${API_BASE}/orders/${relatedOrderId}/details`,
                  { cache: "no-store" },
                );
                if (detailsResponse.ok) relatedDetails = await detailsResponse.json();
              } catch (_) {}

              const orderItems = Array.isArray(relatedDetails?.items)
                ? relatedDetails.items
                : await fetchOrderItemsWithVariations(relatedOrderId);
              const relatedMeta = relatedDetails?.order || relatedOrder;
              aggregatedInvoiceTotal += toNumberOrZero(
                relatedMeta?.total_amount ?? relatedOrder?.total_amount,
              );
              aggregatedDiscount += toNumberOrZero(
                relatedMeta?.discount_amount ?? relatedOrder?.discount_amount,
              );
              if (relatedDetails?.reserve) reserveProofs.push(relatedDetails.reserve);
              if (!orderItems.length) continue;

              const normalizedItems = orderItems.map((item) => {
                const extras = Array.isArray(item.extras) ? item.extras : [];
                const extrasTotal = extras.reduce((sum, extra) => {
                  const qty = Number(extra?.quantity) || 1;
                  const base = Number(extra?.unit_price ?? extra?.price ?? 0) || 0;
                  const total =
                    extra?.total_price != null
                      ? Number(extra.total_price) || 0
                      : base * qty;
                  return sum + total;
                }, 0);

                const quantity = Number(item.quantity) || 1;
                const unitPrice =
                  Number(
                    item.unit_price ??
                      item.price ??
                      item.product_price ??
                      item.base_price ??
                      0,
                  ) || 0;
                const baseTotal =
                  Number(item.total_price) || unitPrice * quantity;

                return {
                  ...item,
                  variation_label:
                    item.variation_label ||
                    item.variationLabel ||
                    item.variation_name ||
                    item.variation_name_ref ||
                    item.variationNameRef ||
                    item.variant_name ||
                    item.variation_text,
                  variation_name_ref:
                    item.variation_name_ref ||
                    item.variationNameRef ||
                    item.variation_name ||
                    item.variationLabel,
                  variation_attributes:
                    item.variation_attributes ||
                    item.variationAttributes ||
                    item.variation_attributes_ref ||
                    item.variationAttributesRef,
                  variation_attributes_ref:
                    item.variation_attributes_ref ||
                    item.variationAttributesRef ||
                    item.variation_attributes ||
                    item.variationAttributes,
                  extras,
                  quantity,
                  unit_price: unitPrice,
                  total_price: baseTotal,
                  extras_total: extrasTotal,
                  total_price_with_extras: baseTotal + extrasTotal,
                  cash_entry_id: cashEntryId,
                  order_id: relatedOrderId,
                };
              });

              aggregatedItems.push(...normalizedItems);
            }

            if (aggregatedItems.length > 0) {
              publishDetails({
                order: {
                  id: resolvedOrderId,
                  cash_entry_id: cashEntryId,
                  table_number:
                    transactionData?.tableNumberRaw ||
                    transactionData?.tableNumber ||
                    "N/A",
                  cashier_name: transactionData?.cashierName || "N/A",
                  total_amount:
                    aggregatedInvoiceTotal || transactionData?.amount || 0,
                  discount_amount: aggregatedDiscount,
                  amount_received: transactionData?.amountReceived ?? 0,
                  change_given: transactionData?.change ?? 0,
                  created_at: transactionData?.time || new Date(),
                },
                items: aggregatedItems,
                reserve: reserveProofs[0] || transactionData?.reserve || null,
              });
              console.log(
                `✅ Articles agrégés via cash_entry_id ${cashEntryId}:`,
                aggregatedItems.length,
              );
              return loadedDetails;
            }
          }
        } catch (cashEntryError) {
          console.log(
            "⚠️ Agrégation par cash_entry_id échouée:",
            cashEntryError?.message || cashEntryError,
          );
        }
      }

      // 1) Utiliser le même endpoint que l'admin (plus fiable et contient les déclinaisons)
      // NOTE: /api/orders/:id/items ne renvoie pas les champs variation_*.
      try {
        const salesRes = await fetch(
          `${API_BASE}/sales/${resolvedOrderId}/details`,
          { cache: "no-store" },
        );
        if (salesRes.ok) {
          const salesData = await salesRes.json();
          const rawItems = Array.isArray(salesData?.items) ? salesData.items : [];
          const normalizedItems = rawItems.map((item) => {
            const extras = Array.isArray(item.extras) ? item.extras : [];
            const extrasTotal = extras.reduce((sum, extra) => {
              const qty = Number(extra.quantity) || 1;
              const base = Number(extra.unit_price ?? extra.price ?? 0) || 0;
              const total =
                extra.total_price != null
                  ? Number(extra.total_price) || 0
                  : base * qty;
              return sum + total;
            }, 0);

            const quantity = Number(item.quantity) || 1;
            const unitPrice =
              Number(
                item.unit_price ??
                  item.price ??
                  item.product_price ??
                  item.base_price ??
                  0,
              ) || 0;
            const baseTotal =
              Number(item.total_price) || unitPrice * quantity;

            return {
              ...item,
              extras,
              quantity,
              unit_price: unitPrice,
              total_price: baseTotal,
              extras_total: extrasTotal,
              total_price_with_extras: baseTotal + extrasTotal,
            };
          });

          publishDetails({
            ...salesData,
            order: salesData?.order || {
              id: resolvedOrderId,
              table_number: transactionData?.tableNumber || "N/A",
              cashier_name: transactionData?.cashierName || "N/A",
              total_amount: transactionData?.amount || 0,
              created_at: transactionData?.time || new Date(),
            },
            items: normalizedItems,
          });

          console.log("✅ Détails vente (sales) chargés:", {
            orderId: resolvedOrderId,
            items: normalizedItems.length,
          });
          return loadedDetails;
        }
      } catch (e) {
        console.log("⚠️ Détails vente (sales) indisponibles:", e?.message || e);
      }

      // Heuristique: pour un encaissement de table (dine-in), agréger les articles de toutes
      // les commandes payées du même service autour de l'heure de paiement.
      const isDineIn =
        transactionData?.tableNumber &&
        /^[0-9]+$/.test(`${transactionData.tableNumber}`);
      if (isDineIn) {
        try {
          // 1) Résoudre l'id de la table par son numéro
          const tablesRes = await fetch(`${API_BASE}/tables`);
          const tables = tablesRes.ok ? await tablesRes.json() : [];
          const tableMatch = (tables || []).find(
            (t) => `${t.number}` === `${transactionData.tableNumber}`,
          );

          if (tableMatch?.id) {
            // 2) Charger toutes les commandes et filtrer les payées pour cette table autour de l'heure
            const ordersRes = await fetch(`${API_BASE}/orders`);
            const orders = ordersRes.ok ? await ordersRes.json() : [];
            const centerTs = new Date(transactionData.time || new Date()).getTime();
            const windowMs = 10 * 60 * 1000; // 10 minutes

            const paidOrders = (orders || []).filter((o) => {
              if (!o) return false;
              if (o.payment_status !== "paid") return false;
              if (o.table_id !== tableMatch.id) return false;
              const t = new Date(o.updated_at || o.created_at || Date.now()).getTime();
              return Math.abs(t - centerTs) <= windowMs;
            });

            // 3) Récupérer les items pour chaque commande payée et agréger
            const itemsArrays = [];
            for (const po of paidOrders) {
              try {
                const orderItems = await fetchOrderItemsWithVariations(po.id);
                if (!orderItems.length) continue;

                const normalizedItems = orderItems.map((item) => {
                  const extras = item.extras || [];
                  const extrasTotal = extras.reduce(
                    (sum, extra) =>
                      sum +
                      (extra.total_price ||
                        extra.price ||
                        extra.unit_price ||
                        0),
                    0,
                  );
                  return {
                    ...item,
                    variation_label:
                      item.variation_label ||
                      item.variationLabel ||
                      item.variation_name ||
                      item.variant_name ||
                      item.variation_text,
                    variation_attributes:
                      item.variation_attributes || item.variationAttributes,
                    name: item.name || item.product_name || "Article inconnu",
                    product_name: item.product_name || item.name || "Article inconnu",
                    unit_price:
                      item.unit_price || item.price || item.display_price || 0,
                    quantity: item.quantity || 1,
                    total_price:
                      item.total_price ||
                      item.unit_price * (item.quantity || 1) ||
                      item.price * (item.quantity || 1) ||
                      0,
                    total_price_with_extras:
                      item.total_price_with_extras ||
                      (item.total_price ||
                        item.unit_price * (item.quantity || 1) ||
                        item.price * (item.quantity || 1) ||
                        0) +
                        extrasTotal,
                    extras,
                    extras_total: extrasTotal,
                  };
                });

                itemsArrays.push(...normalizedItems);
              } catch (itemsError) {
                console.log(
                  "⚠️ Impossible de récupérer les articles agrégés:",
                  itemsError.message,
                );
              }
            }

            if (itemsArrays.length > 0) {
              const completeData = {
                order: {
                  id: resolvedOrderId,
                  table_number: transactionData?.tableNumber || "N/A",
                  cashier_name: transactionData?.cashierName || "N/A",
                  total_amount: transactionData?.amount || 0,
                  discount_amount: toNumberOrZero(transactionData?.discountAmount),
                  created_at: transactionData?.time || new Date(),
                },
                items: itemsArrays,
              };
              publishDetails(completeData);
              console.log(
                `✅ Articles agrégés pour table ${transactionData.tableNumber}:`,
                itemsArrays.length,
                itemsArrays.slice(0, 3),
              );
              return loadedDetails;
            }
          }
        } catch (aggError) {
          console.log("⚠️ Agrégation items dine-in échouée:", aggError.message);
        }
      }

      // Essayer de récupérer les vrais articles pour une commande simple
      try {
        // 1) Tentative avec l'endpoint de détails complet (plus riche en infos de variation)
        try {
          const detailsRes = await fetch(
            `${API_BASE}/orders/${resolvedOrderId}/details`,
            { cache: "no-store" },
          );
          if (detailsRes.ok) {
            const detailsData = await detailsRes.json();
            const itemsFromDetails =
              detailsData?.items ||
              detailsData?.order?.items ||
              detailsData?.orderItems ||
              [];
            if (itemsFromDetails.length > 0) {
              const formatted = itemsFromDetails.map((item) => {
                const extras = item.extras || [];
                const extrasTotal = extras.reduce(
                  (sum, ext) => sum + (ext.total_price || ext.price || 0),
                  0,
                );
                return {
                  ...item,
                  variation_label:
                    item.variation_label ||
                    item.variationLabel ||
                    item.variation_name ||
                    item.variant_name ||
                    item.variation_text,
                  variation_attributes:
                    item.variation_attributes || item.variationAttributes,
                  name: item.name || item.product_name || "Article inconnu",
                  product_name:
                    item.product_name || item.name || "Article inconnu",
                  unit_price:
                    item.unit_price ||
                    item.price ||
                    item.display_price ||
                    0,
                  quantity: item.quantity || 1,
                  extras,
                  extras_total: extrasTotal,
                  total_price:
                    item.total_price ||
                    item.unit_price * (item.quantity || 1) ||
                    item.price * (item.quantity || 1) ||
                    0,
                  total_price_with_extras:
                    item.total_price_with_extras ||
                    (item.total_price ||
                      item.unit_price * (item.quantity || 1) ||
                      item.price * (item.quantity || 1) ||
                      0) +
                      extrasTotal,
                };
              });

              const orderMeta = detailsData.order || {};
              publishDetails({
                order: {
                  id: resolvedOrderId,
                  table_number:
                    orderMeta.table_number ||
                    orderMeta.tableNumber ||
                    transactionData?.tableNumber ||
                    "N/A",
                  cashier_name:
                    orderMeta.cashier_name ||
                    orderMeta.cashierName ||
                    transactionData?.cashierName ||
                    "N/A",
                  total_amount:
                    orderMeta.total_amount ||
                    orderMeta.amount ||
                    transactionData?.amount ||
                    0,
                  discount_amount:
                    toNumberOrZero(orderMeta.discount_amount) ||
                    toNumberOrZero(orderMeta.discountAmount) ||
                    toNumberOrZero(transactionData?.discountAmount),
                  created_at:
                    orderMeta.created_at ||
                    orderMeta.updated_at ||
                    transactionData?.time ||
                    new Date(),
                },
                items: formatted,
                reserve: detailsData?.reserve || transactionData?.reserve || null,
              });

              console.log("ORDER_DETAILS_DEBUG (details)", formatted);
              return loadedDetails;
            }
          }
        } catch (e) {
          console.log("⚠️ Détails commande indisponibles:", e.message);
        }

        // 2) Fallback /items si les détails échouent
        const response = await fetch(`${API_BASE}/orders/${resolvedOrderId}/items`, {
          cache: "no-store",
        });
        if (response.ok) {
          const data = await response.json();
          console.log("ORDER_DETAILS_DEBUG", data?.items);
          if (data.items && data.items.length > 0) {
              const formattedItems = data.items.map((item) => {
                const extrasTotal = (item.extras || []).reduce(
                  (sum, extra) => sum + (extra.total_price || 0),
                  0,
                );
                return {
                  ...item,
                  variation_label:
                    item.variation_label ||
                    item.variationLabel ||
                    item.variation_name ||
                    item.variant_name ||
                    item.variation_text,
                  variation_attributes:
                    item.variation_attributes || item.variationAttributes,
                  name: item.name || item.product_name || "Article inconnu",
                  product_name: item.product_name || item.name || "Article inconnu",
                  unit_price: item.unit_price || 0,
                  quantity: item.quantity || 1,
                  total_price:
                    item.total_price || item.unit_price * item.quantity || 0,
                  total_price_with_extras:
                    item.total_price_with_extras ||
                    (item.total_price || item.unit_price * item.quantity || 0) +
                      extrasTotal,
                  extras: item.extras || [],
                  extras_total: extrasTotal,
                };
              });

            const completeData = {
              order: {
                id: resolvedOrderId,
                table_number: transactionData?.tableNumber || "N/A",
                cashier_name: transactionData?.cashierName || "N/A",
                total_amount: transactionData?.amount || 0,
                discount_amount: toNumberOrZero(transactionData?.discountAmount),
                created_at: transactionData?.time || new Date(),
              },
              items: formattedItems,
            };
            publishDetails(completeData);
            console.log("✅ Vrais articles chargés (simple):", completeData.items);
            return loadedDetails;
          }
        }
      } catch (serverError) {
        console.log(
          "⚠️ Erreur serveur, utilisation de données de base:",
          serverError.message,
        );
      }

      // Fallback: créer des données basiques
      if (verifiedOnly) throw new Error('Impossible de charger les articles du ticket. Réimpression interrompue.');
      const fallbackData = {
        order: {
          id: resolvedOrderId,
          table_number: transactionData?.tableNumber || "N/A",
          cashier_name: transactionData?.cashierName || "N/A",
          total_amount: transactionData?.amount || 0,
          discount_amount: toNumberOrZero(transactionData?.discountAmount),
          created_at: transactionData?.time || new Date(),
          payment_breakdown: transactionData?.paymentBreakdown || null,
        },
        items: [
          {
            product_name:
              "Article de la commande " +
              (transactionData?.commandeId || "N/A"),
            unit_price: transactionData?.amount || 0,
            quantity: 1,
            total_price: transactionData?.amount || 0,
            extras: [
              { extra_name: "Sauce piquante" },
              { extra_name: "Fromage supplémentaire" },
            ],
          },
        ],
      };

      publishDetails(fallbackData);
      console.log("✅ Données de base utilisées:", fallbackData);
    } catch (error) {
      console.error("❌ Erreur chargement détails:", error);
      if (verifiedOnly || transactionData?.settlementId || transactionData?.settlement_id) {
        publishDetails(null);
        if (requestId === orderDetailsRequest.current) setOrderDetailsError(error.message);
        return null;
      }
      // Données minimales de secours
      publishDetails({
        order: {
          id: orderId,
          table_number: transactionData?.tableNumber || "N/A",
          discount_amount: toNumberOrZero(transactionData?.discountAmount),
        },
        items: [
          {
            product_name: "Erreur de chargement",
            unit_price: 0,
            quantity: 1,
            total_price: transactionData?.amount || 0,
            extras: [],
          },
        ],
      });
    } finally {
      if (requestId === orderDetailsRequest.current) setLoadingOrderDetails(false);
    }
    return loadedDetails;
  };

  // Fonction de réimpression du ticket
  const handleReprintTicket = async (order, verifiedDetails = null) => {
    try {
      if (isReprinting) return false;
      setIsReprinting(true);

      const tx = order || selectedOrder || {};
      // Reload the exact split receipt at print time too: the displayed detail
      // may predate another consignment or belong to a previous selection.
      const details = (await loadJournalSettlementDetails(tx)) || verifiedDetails || orderDetails || {};
      const orderMeta = details?.order || {};
      const items = Array.isArray(details?.items) ? details.items : [];

      const settings = await fetchReceiptPrinterSettingsSafe();

      const restaurantName = String(restaurantInfo?.name || "Restaurant").trim();
      const restaurantAddress = String(restaurantInfo?.address || "").trim();
      const restaurantPhone = String(restaurantInfo?.phone || "").trim();

      const paymentNumberRaw =
        orderMeta?.payment_number ||
        tx?.realCommandeId ||
        tx?.commandeId ||
        tx?.payment_number ||
        null;
      const paymentNumber = paymentNumberRaw
        ? String(paymentNumberRaw).replace(/^#\s*/, "").trim()
        : null;

      const tableNumberRaw =
        orderMeta?.table_number ||
        tx?.tableNumberRaw ||
        tx?.tableNumber ||
        tx?.table_number ||
        null;
      const tableNumber =
        tableNumberRaw !== null && tableNumberRaw !== undefined
          ? String(tableNumberRaw).match(/(\d+)/)?.[1] || String(tableNumberRaw).trim()
          : "—";

      const cashierName =
        shortPersonName(orderMeta?.cashier_name) ||
        shortPersonName(tx?.cashierName) ||
        getUserShortLabel(currentUser) ||
        "Caissier";
      const serverName =
        shortPersonName(orderMeta?.server_name) ||
        shortPersonName(tx?.serverName) ||
        shortPersonName(orderMeta?.cashier_name) ||
        cashierName;

      const orderSubtotalRaw = toNumberOrZero(orderMeta?.subtotal);
      const itemsSubtotal = computeItemsInvoiceTotal(items);
      const total = computeInvoiceTotal(details, tx);
      const subtotal =
        orderSubtotalRaw > 0
          ? orderSubtotalRaw
          : itemsSubtotal > 0
            ? itemsSubtotal
            : total;

      const storedDiscount = toNumberOrZero(orderMeta?.discount_amount);
      const inferredDiscount =
        subtotal > 0 && total > 0 ? Math.max(0, subtotal - total) : 0;
      const discount = Math.max(storedDiscount, inferredDiscount);

      const amountReceived = details.settlement_id ? toNumberOrZero(orderMeta?.amount_received) :
        toNumberOrZero(orderMeta?.amount_received) ||
        toNumberOrZero(tx?.amountReceived);
      const changeGiven = details.settlement_id ? toNumberOrZero(orderMeta?.change_given) :
        toNumberOrZero(orderMeta?.change_given) || toNumberOrZero(tx?.change);
      let reserveProof = details.settlement_id ? details.reserve : details?.reserve || orderMeta?.reserve || tx?.reserve || null;
      const proofReference = details.payment_reference || tx.paymentReference;
      const proofQuery = new URLSearchParams(proofReference ? { paymentReference: proofReference }
        : { receiptNumber: String(paymentNumber || '') });
      if (!details.settlement_id) {
        const proofResponse = await apiFetch(`/api/reserves/proof?${proofQuery}`, { includeBearer: true });
        if (proofResponse.ok) reserveProof = await proofResponse.json();
        else if (proofResponse.status === 404) reserveProof = null;
        else throw new Error('Impossible de vérifier les informations de consignation du reçu');
      }
      const reserveRedeemedAmount =
        reserveProof?.mode === "redeem"
          ? toNumberOrZero(reserveProof.reserveMinor) / 100
          : toNumberOrZero(tx?.reserveRedeemedAmount);
      const collectedAmount = toNumberOrZero(
        tx?.collectedAmount ?? Math.max(0, total - reserveRedeemedAmount),
      );

      const breakdownRaw =
        (details.settlement_id ? orderMeta?.payment_breakdown : tx?.paymentBreakdown) ||
        orderMeta?.payment_breakdown ||
        orderMeta?.payment_methods ||
        null;
      let paymentBreakdown = [];
      try {
        const parsed =
          typeof breakdownRaw === "string" ? JSON.parse(breakdownRaw) : breakdownRaw;
        if (Array.isArray(parsed)) paymentBreakdown = parsed;
      } catch (_) {}
      if (!paymentBreakdown.length) {
        paymentBreakdown = Array.isArray(tx?.paymentBreakdown) ? tx.paymentBreakdown : [];
      }
      if (!paymentBreakdown.length && tx?.paymentMethod) {
        paymentBreakdown = [{ methodId: tx.paymentMethod, amount: total }];
      }

      let paymentMethodsForPrint =
        (paymentBreakdown || [])
          .map((pm) => {
            const code = pm?.methodId ?? pm?.method ?? pm?.code ?? pm?.name ?? "";
            if (String(code).trim().toLowerCase() === "reserve") return null;
            const info = getPaymentMethodInfo(code);
            const amount = toNumberOrZero(pm?.amount);
            const isZeroSettlement = ["fully_discounted", "complimentary"].includes(
              String(code || "").trim().toLowerCase(),
            );
            if (!info?.name || (amount <= 0 && !isZeroSettlement)) return null;
            return { method: info.name, amount };
          })
          .filter(Boolean);
      if (!paymentMethodsForPrint.length && reserveRedeemedAmount > 0 && collectedAmount <= 0) {
        paymentMethodsForPrint = [{ method: "Réserve", amount: reserveRedeemedAmount }];
      }

      const printPayload = {
        restaurant_name: restaurantName,
        restaurant_address: restaurantAddress,
        restaurant_phone: restaurantPhone,
        payment_number: paymentNumber,
        table_number: tableNumber,
        cashier_name: cashierName,
        server_name: serverName,
        timestamp: orderMeta?.paid_at || orderMeta?.created_at || tx?.time || new Date(),
        items: items.map((it) => ({
          ...it,
          display_name: formatVariationDisplay(it),
          extras: Array.isArray(it.extras)
            ? it.extras.map((ex) => ({
                name: ex?.name || ex?.extra_name || ex?.extraName,
                price: ex?.price ?? ex?.unit_price ?? ex?.extra_price ?? 0,
                quantity: ex?.quantity ?? 1,
                type: ex?.type || null,
              }))
            : [],
        })),
        subtotal,
        discount,
        total,
        amount_received: amountReceived,
        change_given: changeGiven,
        payment_methods: paymentMethodsForPrint,
        reserve: reserveProof,
      };

      const receiptConnectionType = String(
        settings?.connection_type || "",
      ).toLowerCase();
      if (
        receiptConnectionType === "ethernet" ||
        receiptConnectionType === "direct_rj45"
      ) {
        const response = await fetch(`${API_BASE}/receipt-printer/print-receipt`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(printPayload),
        });
        if (!response.ok) {
          const errText = await response.text();
          console.error("❌ Erreur réimpression (ethernet):", response.status, errText);
          window.alert("La réimpression n'a pas abouti (voir console).");
          return false;
        }
        console.log("✅ Reçu envoyé à l'imprimante (ethernet)");
        return true;
      }

      const showDiscount = toNumberOrZero(printPayload.discount) > 0.01;
      const paidAt = new Date(printPayload.timestamp || Date.now());

      const computeItemTotal = (item) => {
        const quantity = toNumberOrZero(item?.quantity) || 1;
        const unitPrice = toNumberOrZero(item?.unit_price ?? item?.price ?? item?.product_price ?? 0);
        const baseTotal = toNumberOrZero(item?.total_price) || unitPrice * quantity;
        const extrasTotal = computeExtrasTotal(item?.extras);
        return baseTotal + extrasTotal;
      };

      const receiptHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />
            <title>Reçu</title>
            <style>
              body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 20px; font-size: 12px; color: #000; background: #fff; font-weight: 700; }
              .receipt { max-width: 420px; margin: 0 auto; background: #fff; border-radius: 12px; padding: 16px; border: 1px solid #000; }
              .header { text-align: center; margin-bottom: 14px; }
              .brand { font-size: 18px; letter-spacing: 0.02em; }
              .subtitle { font-size: 11px; color: #000; margin-top: 3px; font-weight: 600; }
              .header-contact { font-size: 13px; color: #000; margin-top: 3px; font-weight: 900; }
              .section { margin-bottom: 12px; }
              .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #000; margin-bottom: 6px; font-weight: 700; }
              .item { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #000; font-size: 15px; font-weight: 900; }
              .item:last-child { border-bottom: none; }
              .item-name { color: #000; flex: 1 1 auto; min-width: 0; font-weight: 900; font-size: 15px; }
              .item-extras { font-size: 11px; color: #000; margin-top: 2px; font-weight: 700; }
              .price { color: #000; font-weight: 900; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; min-width: 100px; text-align: right; white-space: nowrap; flex: 0 0 auto; font-size: 15px; font-variant-numeric: tabular-nums; }
              .totals .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 14px; }
              .totals .row.total { font-size: 16px; color: #000; }
              .divider { height: 1px; background: #000; opacity: 0.25; margin: 10px 0; }
              .footer { text-align: center; font-size: 10px; color: #000; margin-top: 12px; font-weight: 600; }
              @media print { body { background: #fff; } }
            </style>
          </head>
          <body>
            <div class="receipt">
              <div class="header">
                <div class="brand">${restaurantName}</div>
                ${restaurantAddress ? `<div class="header-contact">${restaurantAddress}</div>` : ""}
                ${restaurantPhone ? `<div class="header-contact">Tel: ${restaurantPhone}</div>` : ""}
              </div>

              <div class="section">
                <div class="section-title">COMMANDE${paymentNumber ? ` N° ${paymentNumber}` : ""}</div>
                ${isBoutique ? "" : `<div class="subtitle">Table : ${tableNumber}</div>`}
                <div class="subtitle">Caissier : ${cashierName}</div>
                ${isBoutique ? "" : `<div class="subtitle">Serveur : ${serverName}</div>`}
                <div class="subtitle">Date : ${paidAt.toLocaleString("fr-FR").replace(/\\u202F|\\u00A0/g, " ")}</div>
              </div>

              <div class="section">
                <div class="section-title">ARTICLES</div>
                ${items
                  .map((item) => {
                    const displayName = formatVariationDisplay(item);
                    const itemTotal = computeItemTotal(item);
                    const extras =
                      Array.isArray(item.extras) && item.extras.length > 0
                        ? item.extras
                            .map((extra) => {
                              const qty = toNumberOrZero(extra?.quantity) || 1;
                              const price = toNumberOrZero(extra?.price ?? extra?.unit_price ?? extra?.extra_price ?? 0);
                              const amount = price * qty;
                              const label = extra?.name || extra?.extra_name || extra?.extraName || "Extra";
                              return `${label}${qty > 1 ? ` ×${qty}` : ""} (${formatMoneyAscii(amount)} F)`;
                            })
                            .join(", ")
                        : "";
                    return `
                      <div class="item">
                        <div>
                          <div class="item-name">${toNumberOrZero(item.quantity) || 1}× ${displayName}</div>
                          ${extras ? `<div class="item-extras">Suppléments : ${extras}</div>` : ""}
                        </div>
                        <div class="price">${formatMoneyAscii(itemTotal)} F</div>
                      </div>
                    `;
                  })
                  .join("")}
              </div>

              <div class="section totals">
                <div class="row"><span>Sous-total</span><span>${formatMoneyAscii(subtotal)} F</span></div>
                ${showDiscount ? `<div class="row"><span>Réduction</span><span>-${formatMoneyAscii(discount)} F</span></div>` : ""}
                <div class="row total"><span>Total</span><span>${formatMoneyAscii(total)} F</span></div>

                <div style="margin-top: 10px;"></div>
                <div class="section-title">MODE DE PAIEMENT</div>
                ${
                  (paymentMethodsForPrint.length ? paymentMethodsForPrint : [{ method: "Espèces", amount: amountReceived || total }])
                    .map((pm) => `<div style="display:flex;justify-content:space-between;margin:4px 0;"><span>${pm.method}</span><span class="price" style="min-width:auto">${formatMoneyAscii(pm.amount)} F</span></div>`)
                    .join("")
                }

                <div class="divider"></div>
                <div class="row"><span>Reçu</span><span>${formatMoneyAscii(amountReceived || total)} F</span></div>
                <div class="row"><span>Rendu</span><span>${formatMoneyAscii(changeGiven)} F</span></div>
              </div>

              <div class="footer">
                <div>Merci pour votre visite !</div>
                <div>Powered by MOO Sync - ${new Date().getFullYear()}</div>
              </div>
            </div>
          </body>
        </html>
      `;

      void receiptHtml; // Modèle historique conservé temporairement; rendu USB canonique ci-dessous.
      await printHtmlViaUsbPrinter(
        renderUsbTicketHtml({
          kind: "receipt",
          payload: printPayload,
          paperWidthMm: resolveUsbPrintOptions(settings).paperWidthMm,
        }),
        settings,
      );
      console.log("✅ Reçu prêt à imprimer (USB)");
      return true;
    } catch (error) {
      console.error("❌ Erreur réimpression ticket:", error);
      window.alert("Erreur pendant la réimpression (voir console).");
      return false;
    } finally {
      setIsReprinting(false);
    }
  };

  // Obtenir le label et les informations du moyen de paiement
  const getPaymentMethodInfo = (methodCode) => {
    const normalizedCode = (methodCode || "").toString().trim().toLowerCase();
    const method = paymentMethods.find(
      (m) => (m.code || "").toString().toLowerCase() === normalizedCode,
    );
    if (method) {
      return {
        name: method.name,
        color:
          method.name?.toLowerCase() === "espèces" ||
          method.code === "cash"
            ? "#D1D5DB"
            : method.color,
        icon: method.icon,
      };
    }

    // Fallback pour les anciens moyens ou codes non trouvés
    const contains = (token) => normalizedCode.includes(token);
    if (normalizedCode === "fully_discounted") {
      return { name: "Remise 100 %", color: "#10B981", icon: Receipt };
    }
    if (normalizedCode === "complimentary") {
      return { name: "Offert", color: "#10B981", icon: Receipt };
    }
    if (normalizedCode === "reserve") {
      return { name: "Réserve", color: "#F59E0B", icon: Receipt };
    }
    if (
      contains("esp") ||
      contains("cash") ||
      contains("espece") ||
      contains("espèces")
    ) {
      return { name: "Espèces", color: "#D1D5DB", icon: Banknote };
    }
    if (
      contains("carte") ||
      contains("cb") ||
      contains("visa") ||
      contains("master") ||
      contains("card")
    ) {
      return { name: "Carte bancaire", color: "#3B82F6", icon: CreditCard };
    }
    if (contains("orange") || normalizedCode === "om" || normalizedCode === "o.m") {
      return { name: "Orange Money", color: "#F59E0B", icon: Smartphone };
    }
    if (contains("wave")) {
      return { name: "Wave", color: "#0EA5E9", icon: Smartphone };
    }
    if (contains("mtn") || contains("momo")) {
      return { name: "MTN MoMo", color: "#FACC15", icon: Smartphone };
    }
    if (contains("moov") || contains("flooz")) {
      return { name: "Moov Money", color: "#22D3EE", icon: Smartphone };
    }
    if (contains("free")) {
      return { name: "Free Money", color: "#EF4444", icon: Smartphone };
    }
    if (contains("virement") || contains("transfert") || contains("transfer")) {
      return { name: "Virement", color: "#8B5CF6", icon: ArrowLeftRight };
    }
    if (contains("credit") || contains("crédit")) {
      return { name: "À crédit", color: "#7C3AED", icon: Users };
    }
    if (contains("cheque") || contains("chèque")) {
      return { name: "Chèque", color: "#A855F7", icon: Receipt };
    }
    if (contains("multi")) {
      return { name: "Multi-paiement", color: "#22c55e", icon: Receipt };
    }
    if (contains("mobile")) {
      return { name: "Mobile Money", color: "#F59E0B", icon: Smartphone };
    }
    return {
      name: methodCode || "Inconnu",
      color: "#6B7280",
      icon: Receipt,
    };
  };

  // Charger les données à l'ouverture et écouter les événements en temps réel
  useEffect(() => {
    if (isOpen && currentSession && currentUser) {
      setSessionData(currentSession);
      loadSessionData();

      // Écouter les événements Socket.IO pour mise à jour temps réel
      if (window.socket) {
        console.log(
          "🔗 Abonnement aux événements Socket.IO pour journal de caisse",
        );

        const handlePaymentCreated = (data) => {
          console.log("💰 Nouveau paiement détecté:", data);
          // Recharger les données après un délai pour permettre la synchronisation
          setTimeout(() => {
            setCurrentPage(1); // Réinitialiser la pagination
            loadSessionTransactions();
            calculateSummary();
          }, 500);
        };

        const handleOrderUpdated = (data) => {
          console.log("📝 Commande mise à jour:", data);
          // Recharger les données
          setTimeout(() => {
            setCurrentPage(1); // Réinitialiser la pagination
            loadSessionTransactions();
            calculateSummary();
          }, 300);
        };

        window.socket.on("paymentCreated", handlePaymentCreated);
        window.socket.on("orderUpdated", handleOrderUpdated);

        // Nettoyage lors de la fermeture
        return () => {
          window.socket.off("paymentCreated", handlePaymentCreated);
          window.socket.off("orderUpdated", handleOrderUpdated);
        };
      }
    }
  }, [isOpen, currentSession, currentUser]);

  useEffect(() => {
    if (!isOpen || !currentSession || !currentUser) return undefined;
    const handleRefresh = () => {
      setTimeout(() => {
        loadSessionData();
      }, 400);
    };
    window.addEventListener("moo-cash-journal-refresh", handleRefresh);
    return () => {
      window.removeEventListener("moo-cash-journal-refresh", handleRefresh);
    };
  }, [isOpen, currentSession, currentUser]);

  if (!isOpen) return null;

  const selectedInvoiceTotal = selectedOrder
    ? computeInvoiceTotal(orderDetails, selectedOrder)
    : 0;
  const selectedReserve = orderDetails?.settlement_id ? orderDetails.reserve : orderDetails?.reserve || selectedOrder?.reserve || null;
  const selectedReserveRedeemed = selectedOrder
    ? toNumberOrZero(selectedReserve?.mode === "redeem" ? selectedReserve.reserveMinor : 0) /
        100 || toNumberOrZero(selectedOrder.reserveRedeemedAmount)
    : 0;
  const selectedCollectedTotal = selectedOrder
    ? toNumberOrZero(
        selectedOrder.collectedAmount ??
          Math.max(0, selectedInvoiceTotal - selectedReserveRedeemed),
      )
    : 0;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <SaleVoidDialog
        sale={voidSale}
        isOpen={!!voidSale}
        onClose={() => setVoidSale(null)}
        onSuccess={async () => {
          await loadSessionData();
          window.dispatchEvent(new CustomEvent("moo-sales-refresh"));
        }}
      />
      <SaleVoidHistoryDialog
        isOpen={showVoidHistory}
        onClose={() => setShowVoidHistory(false)}
        currentUser={currentUser}
      />
      {/* Journal principal */}
      <div className="relative mx-4 flex h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#020b1f] to-[#020617] text-white">
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          {/* Header */}
          <div className="shrink-0 bg-gradient-to-r from-[#0b1120] via-[#020617] to-[#020617] p-6 border-b border-white/10">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-2xl bg-black/20 border border-white/10 flex items-center justify-center shadow-lg">
                  <Receipt className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                    Journal de caisse
                  </p>
                  <h2 className="mt-1 text-2xl font-bold">Session active</h2>
                  <p className="text-white/80 text-sm">
                    {currentUser?.first_name} {currentUser?.last_name}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Fermer le journal de caisse"
                className="h-11 w-11 shrink-0 rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-colors"
              >
                <X className="text-white" size={22} />
              </button>
            </div>
          </div>

          {/* Corps */}
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <RefreshCw
                    className="text-orange-500 animate-spin mx-auto mb-4"
                    size={48}
                  />
                  <p className="text-white text-lg">
                    Chargement des données...
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Infos session */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-blue-600 rounded-lg">
                        <User className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">
                          Caissier connecté
                        </p>
                        <p className="text-white font-semibold">
                          {currentUser?.first_name} {currentUser?.last_name}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-green-600 rounded-lg">
                        <Clock className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Ouverture</p>
                        <p className="text-white font-semibold">
                          {sessionData?.session_start_time
                            ? new Date(
                                sessionData.session_start_time,
                              ).toLocaleString("fr-FR")
                            : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-purple-600 rounded-lg">
                        <DollarSign className="text-white" size={24} />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">
                          Solde initial
                        </p>
		                        <p className="text-white font-semibold">
		                          {formatCurrency(sessionData?.initial_amount || 0)} F
		                        </p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <button
                        className="flex items-center space-x-2 px-4 py-3 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 transition-colors"
                        onClick={() => setShowCashMovementsModal(true)}
                      >
                        <ArrowLeftRight className="text-white/80" size={20} />
                        <span className="text-white text-sm font-medium">
                          Voir les mouvements
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tableau des transactions */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 max-h-[45vh] overflow-hidden flex flex-col">
                  <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <h3 className="text-xl font-bold flex items-center">
                      <Receipt className="mr-2 text-amber-400" size={22} />
                      Transactions de la session
                    </h3>
                    <div className="flex items-center gap-2">
                      {adminCanDeleteSales && (
                        <button
                          type="button"
                          onClick={() => setShowVoidHistory(true)}
                          className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-white/85 hover:bg-white/10"
                        >
                          <History size={16} />
                          <span>Annulations</span>
                        </button>
                      )}
                      <button
                        onClick={loadSessionData}
                        className="flex items-center space-x-2 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-colors"
                      >
                        <RefreshCw size={16} className="text-white" />
                        <span className="text-white text-sm">Actualiser</span>
                      </button>
                    </div>
                  </div>

                  <div
                    ref={journalScrollContainerRef}
                    onScroll={(event) =>
                      setJournalScrollTop(event.currentTarget.scrollTop || 0)
                    }
                    className="overflow-x-auto overflow-y-auto flex-1"
                  >
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                            Heure
                          </th>
                          {!isBoutique && (
                            <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                              Table
                            </th>
                          )}
                          <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                            Commande
                          </th>
                          <th className="px-4 py-3 text-right text-white/70 text-xs uppercase tracking-wide">
                            Montant
                          </th>
                          <th className="px-4 py-3 text-right text-white/70 text-xs uppercase tracking-wide">
                            Reçu
                          </th>
                          <th className="px-4 py-3 text-right text-white/70 text-xs uppercase tracking-wide">
                            Rendu
                          </th>
                          <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                            Paiement
                          </th>
                          <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                            Caissier
                          </th>
                          <th className="px-4 py-3 text-left text-white/70 text-xs uppercase tracking-wide">
                            Notes
                          </th>
                          <th className="px-4 py-3 text-center text-white/70 text-xs uppercase tracking-wide">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody
                        key={`page-${currentPage}-${startIndex}-${endIndex}`}
                        className="divide-y divide-white/5"
                      >
                        {currentTransactions.length > 0 ? (
                          <>
                            {virtualizedTransactions.topPadding > 0 && (
                              <tr aria-hidden>
                                <td
                                  colSpan={tableColSpan}
                                  style={{
                                    height: `${virtualizedTransactions.topPadding}px`,
                                    padding: 0,
                                    border: 0,
                                  }}
                                />
                              </tr>
                            )}
                            {virtualizedTransactions.rows.map((tx) => {
                            const displayedPayments =
                              Array.isArray(tx.paymentBreakdown) && tx.paymentBreakdown.length
                                ? tx.paymentBreakdown
                                : [{ methodId: tx.paymentMethod, amount: tx.collectedAmount }];
                            return (
                              <tr
                                key={journalTransactionKey(tx)}
                                className="hover:bg-black/20 transition-colors"
                              >
                                <td className="px-4 py-3 text-gray-300 font-mono text-sm">
                                  {formatTime(tx.time)}
                                </td>
                                {!isBoutique && (
                                  <td className="px-4 py-3 text-center text-white font-medium">
                                    {tx.tableNumber}
                                  </td>
                                )}
                                <td className="px-4 py-3 text-amber-300 font-mono text-sm">
                                  <div className="flex items-start space-x-2">
                                    <div>
                                      <span>{tx.commandeId}</span>
                                      {Array.isArray(tx.groupedCommandeIds) &&
                                        tx.groupedCommandeIds.length > 1 && (
                                          <div className="mt-1 max-w-[220px] whitespace-normal break-words text-[10px] leading-tight text-white/45">
                                            Inclut {tx.groupedCommandeIds.join(", ")}
                                          </div>
                                        )}
                                    </div>
                                    <button
                                      onClick={() => {
                                        setSelectedOrder(tx);
                                        setOrderDetails(null);
                                        setShowOrderDetailsModal(true);
                                        loadOrderDetails(tx.id, tx);
                                      }}
                                      className="p-1 hover:bg-white/10 rounded transition-colors"
                                      title="Voir les détails de la vente"
                                    >
                                      <Eye className="text-white/70" size={16} />
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right text-emerald-400 font-semibold font-mono">
                                  {formatCurrency(tx.amount)}
                                </td>
                                <td className="px-4 py-3 text-right text-white font-semibold font-mono">
                                  {formatCurrency(tx.amountReceived)}
                                </td>
                                <td className="px-4 py-3 text-right text-blue-400 font-semibold font-mono">
                                  {tx.change > 0
                                    ? formatCurrency(tx.change)
                                    : "—"}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    {displayedPayments.map((entry, paymentIndex) => {
                                      const paymentInfo = getPaymentMethodInfo(entry.methodId);
                                      const IconComponent = paymentInfo.icon;
                                      return (
                                        <div
                                          key={`${entry.methodId || "payment"}-${paymentIndex}`}
                                          className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 pr-2 text-xs font-semibold text-white/90"
                                        >
                                          <div
                                            className="rounded-full p-1"
                                            style={{ backgroundColor: paymentInfo.color }}
                                          >
                                            <IconComponent className="h-3.5 w-3.5 text-white" />
                                          </div>
                                          <span>{paymentInfo.name}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-white/80 text-sm">
                                  {tx.cashierName}
                                </td>
                                <td className="px-4 py-3 text-white/60 text-sm italic">
                                  {tx.notes || "—"}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {reserveEnabled && !tx.reserveRedeemedAmount && !tx.isCredit && ['caissier','caissiere','cashier','caisse','manager','admin'].includes(String(currentUser?.role || '').toLowerCase()) && (
                                    <button type="button" onClick={() => setReserveTicket(tx)} className="mr-2 rounded-lg border border-amber-300/30 px-2 py-1 text-sm text-amber-200">Consigner</button>
                                  )}
                                  {adminCanDeleteSales && !tx.settlementId && !tx.settlement_id ? (
                                    <button
                                      type="button"
                                      onClick={() => setVoidSale(tx)}
                                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-400/25 bg-red-500/10 text-red-300 transition hover:bg-red-500/20"
                                      title="Annuler cette vente"
                                      aria-label={`Annuler la vente ${tx.commandeId || tx.id}`}
                                    >
                                      <Trash2 size={17} />
                                    </button>
                                  ) : (
                                    <span
                                      className="text-white/25"
                                      title={tx.settlementId || tx.settlement_id
                                        ? "Annulation d’une sous-facture indisponible"
                                        : undefined}
                                    >
                                      —
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                            })}
                            {virtualizedTransactions.bottomPadding > 0 && (
                              <tr aria-hidden>
                                <td
                                  colSpan={tableColSpan}
                                  style={{
                                    height: `${virtualizedTransactions.bottomPadding}px`,
                                    padding: 0,
                                    border: 0,
                                  }}
                                />
                              </tr>
                            )}
                          </>
                        ) : (
                          <tr>
                            <td
                              colSpan={tableColSpan}
                              className="px-4 py-8 text-center text-white/50"
                            >
                              <Receipt
                                className="mx-auto mb-2 text-white/30"
                                size={40}
                              />
                              <p>Aucune transaction pour cette session.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {transactions.length > itemsPerPage && (
                    <div className="px-6 py-4 border-t border-white/10 bg-black/20 rounded-b-2xl">
                      <div className="flex items-center justify-between text-sm text-white/70">
                        <span>
                          Affichage {startIndex + 1}-
                          {Math.min(endIndex, transactions.length)} sur{" "}
                          {transactions.length} transactions
                        </span>
                        <div className="flex items-center space-x-3">
                          <span>
                            Page {currentPage} sur {totalPages}
                          </span>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={goToPreviousPage}
                              disabled={currentPage === 1}
                              className={`p-2 rounded-lg transition-colors ${
                                currentPage === 1
                                  ? "text-white/30 cursor-not-allowed"
                                  : "text-white/80 hover:bg-white/10"
                              }`}
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              onClick={goToNextPage}
                              disabled={currentPage === totalPages}
                              className={`p-2 rounded-lg transition-colors ${
                                currentPage === totalPages
                                  ? "text-white/30 cursor-not-allowed"
                                  : "text-white/80 hover:bg-white/10"
                              }`}
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Résumé */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <TrendingUp className="mr-2 text-emerald-400" size={22} />
                    Résumé de la session
                  </h3>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300/20 bg-amber-300/5 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-white/85">
                        À encaisser
                      </p>
                      <p className="text-xs text-white/50">Commandes non encaissées.</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-amber-200">
                        {formatCurrency(summary.openOrdersValue || 0)} F
                      </p>
                      <p className="text-xs text-white/50">
                        {summary.openOrdersCount || 0} commande
                        {Number(summary.openOrdersCount || 0) !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
                      creditSaleEnabled ? "xl:grid-cols-4" : "xl:grid-cols-3"
                    }`}
                  >
                    <div className="bg-black/25 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-600 rounded-lg">
                          <Receipt className="text-white" size={18} />
                        </div>
                        <div>
                          <p className="text-white/70 text-sm">
                            Total des commandes
                          </p>
                          <p className="text-white text-2xl font-bold">
                            {formatCurrency(summary.totalRevenue)} F
                          </p>
                          <p className="text-white/50 text-xs">
                            {summary.totalOrders} commande
                            {summary.totalOrders > 1 ? "s" : ""}
                          </p>
                          {summary.reserveRedeemedMinor > 0 && (
                            <p className="mt-1 text-xs text-amber-200/80">
                              Net encaissé : {formatCurrency(summary.netCollected)} F
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {creditSaleEnabled && (
                      <div className="bg-black/25 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-violet-600 rounded-lg">
                            <Users className="text-white" size={18} />
                          </div>
                          <div>
                            <p className="text-white/70 text-sm">
                              Paiements à crédit
                            </p>
                            <p className="text-white text-2xl font-bold">
                              {formatCurrency(summary.totalCreditSales || 0)} F
                            </p>
                            <p className="text-white/50 text-xs">
                              Déduit du solde théorique
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="bg-black/25 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-600 rounded-lg">
                          <div className="flex space-x-1">
                            <ArrowUpCircle size={12} className="text-white" />
                            <ArrowDownCircle size={12} className="text-white" />
                          </div>
                        </div>
                        <div>
                          <p className="text-white/70 text-sm">
                            Mouvements de caisse
                          </p>
                          <div className="flex space-x-4 text-sm">
                            <span className="text-emerald-400">
                              +
                              {formatCurrency(
                                movements
                                  .filter((m) => m.type === "deposit")
                                  .reduce(
                                    (sum, m) => sum + parseFloat(m.amount),
                                    0,
                                  ),
                              )}
                            </span>
                            <span className="text-red-400">
                              -
                              {formatCurrency(
                                movements
                                  .filter((m) => m.type === "withdrawal")
                                  .reduce(
                                    (sum, m) => sum + parseFloat(m.amount),
                                    0,
                                  ),
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 border border-emerald-300/40">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <DollarSign className="text-white" size={20} />
                        </div>
                        <div>
                          <p className="text-emerald-50 text-sm">
                            Solde total théorique
                          </p>
                          <p className="text-white text-2xl font-bold">
                            {formatCurrency(summary.theoreticalBalance)} F
                          </p>
                          <p className="text-emerald-100 text-xs">
                            Initial + Dépôts - Retraits + Ventes - Crédit - Réserves utilisées
                          </p>
                        </div>
                      </div>
	                    </div>
	                  </div>
	                </div>
	                </>
	              )}
	          </div>
	        </div>
	      </div>

      {/* Modal des mouvements de caisse */}
      {showCashMovementsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#020b1f] to-[#020617] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-60">
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 p-6 h-[calc(90vh-40px)] overflow-y-auto">
              <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-black/20 border border-white/10 rounded-xl">
                    <ArrowLeftRight className="text-white" size={22} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Mouvements de Caisse
                    </h2>
                    <p className="text-white/80 text-sm">
                      Entrées et sorties d'argent — {currentUser?.first_name}{" "}
                      {currentUser?.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCashMovementsModal(false)}
                  className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-colors"
                >
                  <X className="text-white" size={20} />
                </button>
              </div>

              {movements.length > 0 ? (
                <div className="space-y-4">
                  {movements.map((movement) => (
                    <div
                      key={movement.id}
                      className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10 hover:border-emerald-300/40 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`p-2 rounded-lg ${
                              movement.type === "deposit"
                                ? "bg-green-600 text-white"
                                : "bg-red-600 text-white"
                            }`}
                          >
                            {movement.type === "deposit" ? (
                              <TrendingUp size={20} />
                            ) : (
                              <TrendingDown size={20} />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-semibold">
                              {movement.type === "deposit"
                                ? "Dépôt"
                                : "Retrait"}
                            </p>
                            <p className="text-white/60 text-sm">
                              {new Date(movement.created_at).toLocaleString(
                                "fr-FR",
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-bold text-lg ${
                              movement.type === "deposit"
                                ? "text-green-400"
                                : "text-red-400"
                            }`}
                          >
                            {movement.type === "deposit" ? "+" : "-"}
                            {formatCurrency(movement.amount)} F
                          </p>
                          <p className="text-white/60 text-sm">
                            Par: {movement.cashier_name || "N/A"}
                          </p>
                        </div>
                      </div>
                      {movement.reason && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-white/80 text-sm">
                            <span className="font-semibold">Motif:</span>{" "}
                            {movement.reason}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ArrowLeftRight
                    className="mx-auto mb-4 text-white/30"
                    size={48}
                  />
                  <p className="text-white/80 text-lg">
                    Aucun mouvement de caisse
                  </p>
                  <p className="text-white/60 text-sm mt-1 max-w-md">
                    Aucune entrée ou sortie d'argent n'a été enregistrée pour
                    cette session.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails de vente (caisse) */}
      {reserveTicket && <PostpaidReserveDialog ticket={reserveTicket} onClose={() => setReserveTicket(null)} onSaved={async () => {
        setOrderDetails(null);
        await loadSessionData();
        if (selectedOrder) await loadOrderDetails(selectedOrder.id, selectedOrder);
      }} onPrint={async () => {
        const details = await loadOrderDetails(reserveTicket.id, reserveTicket, true);
        if (!details) throw new Error('Reçu indisponible. Réessayez l’impression.');
        const printed = await handleReprintTicket(reserveTicket, details);
        if (!printed) throw new Error('L’impression n’a pas abouti.');
      }} />}
      {showOrderDetailsModal && selectedOrder && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="relative w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-3xl border border-white/10 shadow-2xl bg-gradient-to-br from-[#020617] via-[#030b1f] to-[#020617] text-white">
            <div className="pointer-events-none absolute inset-0 opacity-50">
              <div className="absolute -top-16 -left-10 h-40 w-40 rounded-full bg-amber-500/15 blur-3xl" />
              <div className="absolute -bottom-20 -right-16 h-48 w-48 rounded-full bg-blue-500/15 blur-3xl" />
            </div>

            <div className="relative z-10 p-6 h-[92vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-6">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                    Détails de la vente
                  </p>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold">
                      {selectedOrder.commandeId}
                    </h2>
                    {!isBoutique && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/10">
                        {selectedOrder.isDelivery ? "Livraison" : "Sur place"}
                      </span>
                    )}
                    {toNumberOrZero(orderDetails?.order?.discount_amount) > 0 && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-500/10 border border-red-400/30 text-red-200">
                        Remise -{formatCurrency(orderDetails?.order?.discount_amount)} F
                      </span>
                    )}
                  </div>
                  {!isBoutique && (
                    <p className="text-white/70 text-sm">
                      {selectedOrder.isDelivery
                        ? selectedOrder.deliveryReferenceDisplay ||
                          selectedOrder.deliveryMethodLabel ||
                          "Livraison"
                        : `Table ${selectedOrder.tableNumberRaw || selectedOrder.tableNumber}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleReprintTicket(selectedOrder)}
                    className="p-2 rounded-xl border border-white/15 bg-white/10 hover:bg-white/20 transition-colors"
                    title="Réimprimer le reçu"
                    disabled={isReprinting || loadingOrderDetails}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
	                      viewBox="0 0 24 24"
	                      fill="none"
	                      stroke="currentColor"
	                      strokeWidth="2"
	                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`text-white ${isReprinting ? "opacity-60" : ""}`}
                    >
                      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
                      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                      <path d="M12 17V7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setShowOrderDetailsModal(false);
                      setSelectedOrder(null);
                      orderDetailsRequest.current += 1;
                      setOrderDetails(null);
                    }}
                    className="p-2 rounded-xl border border-white/15 bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <X size={18} className="text-white" />
                  </button>
                </div>
              </div>

              {loadingOrderDetails ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <RefreshCw className="animate-spin text-amber-400 mx-auto mb-4" size={42} />
                    <p className="text-white/80">Chargement des détails...</p>
                  </div>
                </div>
              ) : orderDetailsError ? <p role="alert" className="py-8 text-red-300">{orderDetailsError}</p> : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-white/60 text-sm">Commande</p>
                      <p className="text-white font-semibold text-lg">
                        {selectedOrder.commandeId}
                      </p>
                      <p className="text-white/70 text-sm mt-1">
                        {formatTime(selectedOrder.time)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-white/60 text-sm">Caissier</p>
                      <p className="text-white font-semibold text-lg">
                        {selectedOrder.cashierName}
                      </p>
                      {!isBoutique && !selectedOrder.isDelivery && (
                        <p className="text-white/70 text-sm mt-1">
                          Table {selectedOrder.tableNumberRaw || selectedOrder.tableNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Receipt size={18} className="text-amber-300" />
                      <h3 className="text-white font-semibold">Articles</h3>
                    </div>
                    <div className="space-y-3">
                      {(orderDetails?.items || []).map((item, idx) => {
                        const extras = Array.isArray(item.extras) ? item.extras : [];
                        const quantity = Number(item.quantity) || 1;
                        const unitPrice = Number(item.unit_price ?? item.price ?? 0) || 0;
                        const baseTotal =
                          Number(item.total_price) || unitPrice * quantity;
                        const computedExtrasTotal = extras.reduce((sum, extra) => {
                          const extraQty = Number(extra.quantity) || 1;
                          const extraUnit =
                            Number(
                              extra.total_price != null
                                ? 0
                                : extra.unit_price ?? extra.price ?? extra.extra_price ?? 0,
                            ) || 0;
                          const extraTotal =
                            extra.total_price != null
                              ? Number(extra.total_price) || 0
                              : extraUnit * extraQty;
                          return sum + extraTotal;
                        }, 0);
                        const extrasTotal =
                          Number(item.extras_total) || computedExtrasTotal;
                        const finalTotal =
                          Number(item.total_price_with_extras) ||
                          Number(item.total_price) + extrasTotal ||
                          baseTotal + extrasTotal;
                        const hasExtras = extras.length > 0;

                        return (
                          <div
                            key={idx}
                            className="rounded-2xl border border-white/10 bg-black/10 p-4 hover:bg-black/15 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <p className="text-white font-semibold leading-snug">
                                  {formatVariationDisplay(item)}
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-white/60">
                                  <span className="inline-flex items-center rounded-lg bg-white/5 border border-white/10 px-2 py-1 font-mono">
                                    {formatCurrency(unitPrice)} F
                                  </span>
                                  <span className="text-white/40">×</span>
                                  <span className="inline-flex items-center rounded-lg bg-white/5 border border-white/10 px-2 py-1 font-mono">
                                    {quantity}
                                  </span>
                                  <span className="text-white/40">=</span>
                                  <span className="font-mono text-white/80">
                                    {formatCurrency(baseTotal)} F
                                  </span>

                                  {hasExtras && (
                                    <span className="inline-flex items-center rounded-full bg-blue-500/10 border border-blue-400/20 px-2 py-1 font-semibold text-blue-200">
                                      + suppléments{" "}
                                      {extrasTotal > 0
                                        ? `${formatCurrency(extrasTotal)} F`
                                        : "offerts"}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="shrink-0 text-right">
                                <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">
                                  Total
                                </p>
                                <p className="text-green-400 font-mono font-bold text-lg leading-tight">
                                  {formatCurrency(finalTotal)} F
                                </p>
                              </div>
                            </div>

                            {hasExtras && (
                              <div className="mt-3 flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                                <div className="min-w-0">
                                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/60">
                                    Suppléments
                                  </p>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {extras.map((extra, i) => {
                                      const extraQty = Number(extra.quantity) || 1;
                                      const extraUnit =
                                        Number(
                                          extra.unit_price ??
                                            extra.price ??
                                            extra.extra_price ??
                                            0,
                                        ) || 0;
                                      const extraTotal =
                                        extra.total_price != null
                                          ? Number(extra.total_price) || 0
                                          : extraUnit * extraQty;
                                      const extraName =
                                        extra.extra_name || extra.name || "Extra";
                                      const isFree = extraTotal <= 0;

                                      return (
                                        <span
                                          key={i}
                                          className="inline-flex max-w-full items-center gap-2 rounded-full bg-black/10 border border-white/10 px-2 py-1 text-xs text-white/80"
                                        >
                                          <span className="truncate">
                                            + {extraName}
                                          </span>
                                          {extraQty > 1 && (
                                            <span className="font-mono text-white/60">
                                              x{extraQty}
                                            </span>
                                          )}
                                          <span
                                            className={`font-mono ${
                                              isFree ? "text-emerald-200" : "text-blue-200"
                                            }`}
                                          >
                                            {isFree
                                              ? "offert"
                                              : `${formatCurrency(extraTotal)} F`}
                                          </span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">
                                    + total
                                  </p>
                                  <p className="font-mono text-blue-200 text-sm">
                                    {extrasTotal > 0
                                      ? `${formatCurrency(extrasTotal)} F`
                                      : "Offert"}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {(orderDetails?.items || []).length === 0 && (
                        <p className="text-white/60 py-4 text-center">
                          Aucun article pour cette vente.
                        </p>
                      )}
                    </div>
                  </div>

			                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-6">
			                    <div className="flex items-center gap-2 mb-3">
			                      <DollarSign size={18} className="text-green-400" />
			                      <h3 className="text-white font-semibold">Paiement</h3>
			                    </div>
			                    <div className="space-y-2">
			                      {(() => {
			                        const storedDiscount = toNumberOrZero(
			                          orderDetails?.order?.discount_amount,
			                        );
			                        const itemsSubtotal = computeItemsInvoiceTotal(
			                          orderDetails?.items,
			                        );
			                        const inferredDiscount =
			                          itemsSubtotal > 0 && selectedInvoiceTotal > 0
			                            ? Math.max(0, itemsSubtotal - selectedInvoiceTotal)
			                            : 0;
			                        const discount =
			                          storedDiscount > 0 ? storedDiscount : inferredDiscount;
			                        if (discount <= 0) return null;
			                        return (
			                          <>
			                            {itemsSubtotal > 0 && (
			                              <div className="flex justify-between text-sm text-white/80">
			                                <span>Sous-total</span>
			                                <span className="font-mono text-white font-semibold">
			                                  {formatCurrency(itemsSubtotal)} F
			                                </span>
			                              </div>
			                            )}
			                            <div className="flex justify-between text-sm text-red-300">
			                              <span>Réduction</span>
			                              <span className="font-mono font-semibold">
			                                -{formatCurrency(discount)} F
			                              </span>
			                            </div>
			                          </>
			                        );
			                      })()}
			                      <div className="flex justify-between text-sm text-white/80">
			                        <span>Total facture</span>
			                        <span className="font-mono text-green-400 font-semibold">
			                          {formatCurrency(selectedInvoiceTotal)} F
			                        </span>
		                      </div>
	                      {selectedReserveRedeemed > 0 && (
	                        <div className="flex justify-between text-sm text-amber-200">
	                          <span>Réserve utilisée</span>
	                          <span className="font-mono font-semibold">
	                            -{formatCurrency(selectedReserveRedeemed)} F
	                          </span>
	                        </div>
	                      )}
	                      {selectedReserveRedeemed > 0 && (
	                        <div className="flex justify-between text-sm text-white/80">
	                          <span>Reste payé</span>
	                          <span className="font-mono text-white font-semibold">
	                            {formatCurrency(selectedCollectedTotal)} F
	                          </span>
	                        </div>
	                      )}
		                      <div className="flex justify-between text-sm text-white/80">
		                        <span>Montant reçu</span>
	                        <span className="font-mono text-white font-semibold">
	                          {formatCurrency(selectedOrder.amountReceived)} F
	                        </span>
	                      </div>
                      <div className="flex justify-between text-sm text-white/80">
                        <span>Monnaie rendue</span>
                        <span className="font-mono text-blue-300 font-semibold">
                          {selectedOrder.change > 0
                            ? `${formatCurrency(selectedOrder.change)} F`
                            : "—"}
                        </span>
                      </div>
                      <div className="border-t border-white/10 pt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-white/70 text-sm">
                            Moyen de paiement
                          </span>
                          <div className="flex flex-wrap gap-2 justify-end">
                            {(() => {
                              const entries =
                                Array.isArray(selectedOrder.paymentBreakdown) &&
                                selectedOrder.paymentBreakdown.length
                                  ? selectedOrder.paymentBreakdown
                                  : buildJournalPaymentBreakdown({
                                      paymentMethod: selectedOrder.paymentMethod,
                                      collectedAmount: selectedCollectedTotal,
                                      reserveRedeemedAmount: selectedReserveRedeemed,
                                    });
                              return entries.map((entry, idx) => {
                                const info = getPaymentMethodInfo(entry.methodId);
                                const Icon = info.icon;
                                return (
                                  <div
                                    key={`${entry.methodId || "payment"}-${idx}`}
                                    className="flex items-center gap-2 rounded-xl bg-white/5 px-2.5 py-1.5"
                                  >
                                    <div
                                      className="p-1 rounded"
                                      style={{ backgroundColor: info.color }}
                                    >
                                      <Icon className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-white text-sm">
                                      {info.name}
                                      {entry.amount ? ` (${formatCurrency(entry.amount)} F)` : ""}
                                    </span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
		                      </div>
		                      </div>
		                    </div>
		                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CashJournalModal;
